import {
  Box,
  Tabs as ChakraTabs,
  Flex,
  Grid,
  HStack,
  Heading,
  Link,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import {
  type ChangeEvent,
  Component,
  type ErrorInfo,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowLeft,
  FiBarChart2,
  FiBatteryCharging,
  FiCode,
  FiDownload,
  FiList,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiSquare,
  FiThermometer,
  FiTrash2,
  FiWifi,
  FiX,
} from "react-icons/fi"

import { TagsService } from "@/client"
import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from "@/components/ui/dialog"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import {
  formatTemperature,
  useTemperatureUnit,
} from "@/hooks/useTemperatureUnit"
import { format } from "date-fns"

import {
  TIMEZONE_LOCAL,
  TimezoneSelector,
  formatInTimezone,
  resolveStoredTimezone,
} from "@/components/Common/TimezoneSelector"

// Tag types that show the Measurements + Tag Readings tabs. The two
// Axzon-family additions (TEMPERATURE_DATALOGGER for OPUS, SENSOR_TAG
// for Magnus-S*) need them too — without these entries OPUS tags lose
// the temperature chart and readings table on the detail page.
const MEASUREMENT_TYPES = [
  "TEMPERATURE",
  "TEMPERATURE_DATALOGGER",
  "SENSOR_TAG",
  "HUMIDITY",
  "GENERIC",
]

export const Route = createFileRoute("/_layout/tag-detail")({
  component: TagDetail,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tagId: search.tagId as string | undefined,
    }
  },
})

function TagDetail() {
  const { t } = useTranslation(["tags", "common"])
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const { unit: tempUnit } = useTemperatureUnit()
  const sc = useSemanticColors()
  const queryClient = useQueryClient()
  const { tagId } = useSearch({ from: "/_layout/tag-detail" })
  const { currentOrganization } = useContext(OrganizationContext)
  const [readTriggered, setReadTriggered] = useState(false)

  // Fetch tag data (polls every 2s during read for progress tracking)
  const { data: tag, isLoading: tagLoading } = useQuery({
    queryKey: ["tag", tagId],
    queryFn: () => TagsService.readTag({ id: tagId! }),
    enabled: !!tagId,
    refetchInterval: readTriggered ? 2000 : false,
  })

  // Use tag's own org for API calls (works even when "All Organizations" is selected)
  const orgId = tag?.organisation_id || currentOrganization?.id

  const effectiveTagType = tag?.tag_type ?? tag?.template?.tag_type ?? ""
  // Durable OPUS-IC signal: a tag read as an OPUS RFID datalogger keeps its
  // temperature logs viewable even after a multimodal/barcode re-scan flips
  // tag_type to MULTIMODAL/BARCODE. Derived from the durable fields already
  // exposed (TID prefix E2C24500 / tag_model / chip_type); mirrors the
  // backend-computed has_opus_ic.
  const hasOpusIc =
    (tag?.tid?.toUpperCase().startsWith("E2C24500") ?? false) ||
    tag?.tag_model === "Axzon OPUS" ||
    ["opus", "axzon opus"].includes((tag?.chip_type ?? "").toLowerCase())
  // Gate temperature/measurement UI on the OPUS-IC signal too — not just the
  // (volatile) tag_type enum — so an OPUS datalogger that became MULTIMODAL
  // still shows its temperature logs.
  const isMeasurementTag =
    MEASUREMENT_TYPES.includes(effectiveTagType) || hasOpusIc
  // Temperature-bearing tag types get the Instant-temp tab; only the
  // datalogger (active-logging, battery-powered) gets the Battery tab.
  const isTemperatureTag =
    hasOpusIc ||
    effectiveTagType === "TEMPERATURE" ||
    effectiveTagType === "TEMPERATURE_DATALOGGER"
  const isDatalogger =
    hasOpusIc || effectiveTagType === "TEMPERATURE_DATALOGGER"

  const showMeasurements = isMeasurementTag
  const effectiveTagId = tagId

  // Fetch first reading timestamp for Grafana time range (only for measurement tags)
  // Fetch all readings for chart (up to 4096 — RFID tag memory limit)
  const { data: chartReadings = [] } = useQuery({
    queryKey: ["tag-chart-readings", effectiveTagId],
    queryFn: () =>
      TagsService.readTagReadings({
        id: effectiveTagId!,
        limit: 4096,
        skip: 0,
      }),
    enabled: !!effectiveTagId && showMeasurements,
  })

  // Per-scan telemetry (TagEvent.data) — sourced for the new Instant
  // temp event and Battery voltage tabs. These are deliberately
  // distinct from chartReadings (READ-batch logs in TagReading); they
  // surface what each SCAN payload carried as a live snapshot.
  const { data: instantTempPoints = [] } = useQuery({
    queryKey: ["tag-scan-telemetry", effectiveTagId, "temperature"],
    queryFn: () =>
      TagsService.readTagScanTelemetry({
        id: effectiveTagId!,
        field: "temperature",
        limit: 4096,
      }),
    enabled: !!effectiveTagId,
  })
  const { data: batteryVoltagePoints = [] } = useQuery({
    queryKey: ["tag-scan-telemetry", effectiveTagId, "battery_voltage"],
    queryFn: () =>
      TagsService.readTagScanTelemetry({
        id: effectiveTagId!,
        field: "battery_voltage",
        limit: 4096,
      }),
    enabled: !!effectiveTagId,
  })

  // Alarm history for this tag (most recent first, capped at 100 rows).
  const { data: tagAlerts = [] } = useQuery({
    queryKey: ["tag-alerts", effectiveTagId],
    queryFn: () =>
      TagsService.readTagAlerts({
        id: effectiveTagId!,
        limit: 100,
      }),
    enabled: !!effectiveTagId && showMeasurements,
    refetchInterval: 30000,
  })

  // Compute contiguous time ranges where the temperature exceeded
  // [min_threshold, max_threshold]. Walks the readings in chronological
  // order; each range is opened when a reading first leaves the band and
  // closed when one returns to it (or at the end of the dataset).
  const tagAny: any = tag
  const alarmRanges = useMemo(() => {
    const min = tagAny?.min_threshold
    const max = tagAny?.max_threshold
    if (
      !chartReadings.length ||
      (typeof min !== "number" && typeof max !== "number")
    ) {
      return [] as Array<{
        start: string
        end: string
        peakValue: number
        durationMs: number
        direction: "over" | "under"
      }>
    }
    const sorted = [...chartReadings].sort(
      (a: any, b: any) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    const ranges: Array<{
      start: string
      end: string
      peakValue: number
      durationMs: number
      direction: "over" | "under"
    }> = []
    let current: {
      startTs: string
      startMs: number
      peak: number
      direction: "over" | "under"
    } | null = null
    for (const r of sorted as any[]) {
      const v = r.value
      const over = typeof max === "number" && typeof v === "number" && v > max
      const under = typeof min === "number" && typeof v === "number" && v < min
      const isAlarm = over || under
      if (isAlarm && !current) {
        current = {
          startTs: r.timestamp,
          startMs: new Date(r.timestamp).getTime(),
          peak: v,
          direction: over ? "over" : "under",
        }
      } else if (isAlarm && current) {
        if (current.direction === "over" && v > current.peak) current.peak = v
        if (current.direction === "under" && v < current.peak) current.peak = v
      } else if (!isAlarm && current) {
        ranges.push({
          start: current.startTs,
          end: r.timestamp,
          peakValue: current.peak,
          durationMs: new Date(r.timestamp).getTime() - current.startMs,
          direction: current.direction,
        })
        current = null
      }
    }
    if (current && sorted.length > 0) {
      const last: any = sorted[sorted.length - 1]
      ranges.push({
        start: current.startTs,
        end: last.timestamp,
        peakValue: current.peak,
        durationMs: new Date(last.timestamp).getTime() - current.startMs,
        direction: current.direction,
      })
    }
    return ranges
  }, [chartReadings, tagAny?.min_threshold, tagAny?.max_threshold])

  // Fetch recent RSSI events for non-measurement tags
  const { data: recentEvents = [], refetch: refetchEvents } = useQuery({
    queryKey: ["tag-recent-events", tagId, currentOrganization?.id],
    queryFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const url = `${base}/api/v1/organizations/${currentOrganization!.id}/tags/recent-activity?minutes=60&limit=20`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return []
      const all = await res.json()
      return all.filter((e: any) => e.tag_id === tagId)
    },
    enabled: !!tagId && !!currentOrganization && !isMeasurementTag,
    refetchInterval: 15000,
  })

  // Fetch real-time tag info
  const fetchInfoMutation = useMutation({
    mutationFn: async () => {
      const result = await TagsService.fetchTagInfo({
        id: tagId!,
        outputFormat: "json",
        timeout: 10,
      })
      return result
    },
    onSuccess: (result) => {
      const hasError =
        result.length === 0 ||
        result.some((r: any) => r.status === "ERROR" || r.error)
      if (hasError) {
        showToast.showErrorToast(t("tags:detail.tagNotFound"))
      } else {
        showToast.showSuccessToast(t("tags:detail.fetchInfoSuccess"))
        queryClient.invalidateQueries({ queryKey: ["tag", tagId] })
      }
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || t("tags:detail.fetchInfoError")
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  // Stop ongoing read/inventory on the reader
  const stopReadMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const url = `${base}/api/v1/organizations/${orgId!}/readers/${tag?.last_seen_by}/stop`
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`Stop failed: ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      showToast.showSuccessToast(t("tags:detail.stopSuccess"))
    },
    onError: (err: any) => {
      showToast.showErrorToast(err.message || t("tags:detail.stopError"))
    },
  })

  const expectedSamplesRef = useRef(0)
  const [infoJsonView, setInfoJsonView] = useState(false)
  const [readingsPage, setReadingsPage] = useState(1)
  const [displayTimezone, setDisplayTimezone] = useState<string>(() =>
    resolveStoredTimezone(localStorage.getItem("tag-detail-timezone")),
  )
  const onChangeTimezone = (tz: string) => {
    setDisplayTimezone(tz)
    localStorage.setItem("tag-detail-timezone", tz)
  }

  const formatTimestamp = (ts: string) =>
    formatInTimezone(ts, displayTimezone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  const pageSizeOptions = [32, 64, 128, 256, 512, 1024, 2048, 0] // 0 = all
  const [readingsPageSize, setReadingsPageSize] = useState(128)

  // Get total count for pagination
  const { data: totalReadingsData } = useQuery({
    queryKey: ["tag-readings-total", effectiveTagId],
    queryFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/tags/${effectiveTagId}/readings/count`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return { count: 0 }
      return res.json() as Promise<{ count: number }>
    },
    enabled: !!effectiveTagId && showMeasurements,
  })
  const totalReadings = totalReadingsData?.count ?? 0
  const totalPages =
    readingsPageSize === 0
      ? 1
      : Math.max(1, Math.ceil(totalReadings / readingsPageSize))

  // Load latest readings for this tag (always visible for measurement tags)
  const {
    data: latestReadings = [],
    isLoading: readingsLoading,
    refetch: refetchLatestReadings,
  } = useQuery({
    queryKey: [
      "tag-readings-latest",
      effectiveTagId,
      readingsPage,
      readingsPageSize,
    ],
    queryFn: () =>
      TagsService.readTagReadings({
        id: effectiveTagId!,
        limit: readingsPageSize === 0 ? 4096 : readingsPageSize,
        skip:
          readingsPageSize === 0 ? 0 : (readingsPage - 1) * readingsPageSize,
      }),
    enabled: !!effectiveTagId && showMeasurements,
  })

  // Poll reading count for progress tracking during read operation
  // Progress tracking: use tag.read_samples_received (updated by ingester for every batch)
  const samplesReceived = tag?.read_samples_received ?? 0
  const expectedSamples = expectedSamplesRef.current
  const readProgress =
    expectedSamples > 0
      ? Math.min(100, (samplesReceived / expectedSamples) * 100)
      : 0

  // Auto-refresh chart, table, and reading count when tag data changes during read
  const prevSamplesRef = useRef(0)
  useEffect(() => {
    if (!readTriggered) return
    if (samplesReceived > prevSamplesRef.current) {
      queryClient.invalidateQueries({
        queryKey: ["tag-chart-readings", effectiveTagId],
      })
      queryClient.invalidateQueries({
        queryKey: ["tag-readings-latest", effectiveTagId],
      })
      queryClient.invalidateQueries({
        queryKey: ["tag-readings-total", effectiveTagId],
      })
    }
    prevSamplesRef.current = samplesReceived
  }, [samplesReceived, readTriggered, effectiveTagId, queryClient])

  // Default to measurements tab for measurement tags, inventory otherwise
  const [activeTab, setActiveTab] = useState("inventory")
  const hasSetInitialTab = useRef(false)
  useEffect(() => {
    if (!hasSetInitialTab.current && tag) {
      hasSetInitialTab.current = true
      if (isMeasurementTag) {
        setActiveTab("measurements")
      }
    }
  }, [tag, isMeasurementTag])
  const inventoryPage = 1
  const inventoryPageSize = 50

  const { data: inventoryEvents = [], isLoading: inventoryLoading } = useQuery({
    queryKey: ["tag-inventory-events", tagId, orgId, inventoryPage],
    queryFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId!}/tags/${tagId}/events?skip=${(inventoryPage - 1) * inventoryPageSize}&limit=${inventoryPageSize}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return []
      return res.json() as Promise<
        Array<{
          tag_id: string
          timestamp: string
          event: string
          reader_id?: string | null
          lon: number
          lat: number
          data?: Record<string, any> | null
        }>
      >
    },
    enabled: !!tagId && !!orgId,
  })

  // Fetch readers to map IDs to names for inventory table
  const { data: readers = [] } = useQuery({
    queryKey: ["readers-for-tag", orgId],
    queryFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const res = await fetch(
        `${base}/api/v1/organizations/${orgId}/readers/?skip=0&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return []
      return res.json() as Promise<Array<{ id: string; name: string }>>
    },
    enabled: !!orgId,
  })

  // Auto-close progress card when read is complete
  useEffect(() => {
    if (readTriggered && expectedSamples > 0 && readProgress >= 100) {
      const timer = setTimeout(() => {
        setReadTriggered(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [readTriggered, readProgress, expectedSamples])

  // Trigger read (fire-and-forget) then poll DB count for progress
  const fetchDataMutation = useMutation({
    mutationFn: async () => {
      // Reset progress tracking
      prevSamplesRef.current = 0

      // Get expected samples: use last info fetch, or auto-fetch info first
      let samples = 0
      const infoData = fetchInfoMutation.data
      if (infoData) {
        const info = infoData.find((r: any) => r.status === "SUCCESS")
        samples = (info?.data as any)?.samples || 0
      }
      if (!samples) {
        try {
          const infoResult = await TagsService.fetchTagInfo({
            id: tagId!,
            outputFormat: "json",
            timeout: 10,
          })
          const info = infoResult?.find((r: any) => r.status === "SUCCESS")
          samples = (info?.data as any)?.samples || 0
        } catch {
          // Info fetch failed — continue without expected count
        }
      }
      expectedSamplesRef.current = samples

      return TagsService.fetchTagData({
        id: tagId!,
        outputFormat: "json",
        limit: 0,
        timeout: 10,
      })
    },
    onSuccess: () => {
      setReadTriggered(true)
      showToast.showSuccessToast(t("tags:detail.readSent"))
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || t("tags:detail.readError")
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const tpl = tag?.template

  // Check if tag has GPS data (from tag model or inventory events)
  const hasGpsData = !!(
    (tag?.last_lat && tag?.last_lon) ||
    inventoryEvents.some((e) => {
      const gps = e.data?.gps as
        | { latitude?: number; longitude?: number }
        | undefined
      return (gps?.latitude && gps?.longitude) || e.lat !== 0 || e.lon !== 0
    })
  )

  // Collect GPS points from inventory events for map
  const gpsPoints = inventoryEvents
    .map((e) => {
      const gps = e.data?.gps as
        | { latitude?: number; longitude?: number }
        | undefined
      const lat = gps?.latitude ?? e.lat
      const lon = gps?.longitude ?? e.lon
      if (lat == null || lon == null || (lat === 0 && lon === 0)) return null
      return { lat, lon, timestamp: e.timestamp, data: e.data }
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (!tagId) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("tags:detail.tagNotFound")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (tagLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (!tag) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("tags:detail.tagNotFound")}
        </Heading>
        <Text color={sc.textMuted}>{t("tags:detail.tagNotFound")}</Text>
      </VisionCard>
    )
  }

  const hasLastReader = !!tag.last_seen_by

  return (
    <Box>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 2, md: 4 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box flex={1} minW={0} w="100%">
          <Heading
            size={{ base: "lg", md: "xl" }}
            color={sc.heading}
            lineClamp={2}
          >
            {tag.name}
          </Heading>
          <Flex gap={2} mt={1} wrap="wrap" align="center">
            <Badge colorScheme={tag.enabled ? "green" : "gray"} size="sm">
              {tag.enabled
                ? t("tags:form.enabled")
                : t("common:status.inactive")}
            </Badge>
            {tag.template && (
              <Badge colorScheme="blue" size="sm">
                {tag.template.tag_type}
              </Badge>
            )}
            <Text
              color={sc.textMuted}
              fontSize="sm"
              fontFamily="mono"
              wordBreak="break-all"
            >
              EPC: {tag.epcid}
              {tag.tag_model && ` · ${tag.tag_model}`}
            </Text>
          </Flex>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/tags" })}
          size="sm"
          alignSelf={{ base: "flex-end", md: "auto" }}
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", lg: "3fr 1fr" }}
        gap={{ base: 4, md: 6 }}
      >
        {/* ══════ LEFT — MAIN CONTENT (75%) ══════ */}
        <VStack gap={6} w="100%" minW={0}>
          {/* Tabs: Readings / Inventory */}
          <ChakraTabs.Root
            value={activeTab}
            onValueChange={(e) => setActiveTab(e.value)}
            w="100%"
          >
            <ChakraTabs.List mb={4}>
              {isMeasurementTag && (
                <ChakraTabs.Trigger value="measurements">
                  <FiBarChart2 style={{ marginRight: "8px" }} />
                  {t("tags:detail.tabMeasurements")}
                </ChakraTabs.Trigger>
              )}
              {isMeasurementTag && (
                <ChakraTabs.Trigger value="tag-readings">
                  <FiActivity style={{ marginRight: "8px" }} />
                  {t("tags:detail.tabTagReadings")}
                </ChakraTabs.Trigger>
              )}
              {isTemperatureTag && (
                <ChakraTabs.Trigger value="instant-temp">
                  <FiThermometer style={{ marginRight: "8px" }} />
                  {t("tags:detail.tabInstantTemp")}
                </ChakraTabs.Trigger>
              )}
              {isDatalogger && (
                <ChakraTabs.Trigger value="battery">
                  <FiBatteryCharging style={{ marginRight: "8px" }} />
                  {t("tags:detail.tabBattery")}
                </ChakraTabs.Trigger>
              )}
              <ChakraTabs.Trigger value="inventory">
                <FiList style={{ marginRight: "8px" }} />
                {t("tags:detail.tabInventory")}
              </ChakraTabs.Trigger>
              <ChakraTabs.Trigger value="details">
                <FiCode style={{ marginRight: "8px" }} />
                {t("tags:detail.tabDetails")}
              </ChakraTabs.Trigger>
              {hasGpsData && (
                <ChakraTabs.Trigger value="map">
                  <FiMapPin style={{ marginRight: "8px" }} />
                  {t("tags:detail.tabMap")}
                </ChakraTabs.Trigger>
              )}
            </ChakraTabs.List>

            {isMeasurementTag && (
              <ChakraTabs.Content value="measurements">
                <VStack gap={6} w="100%">
                  {/* Chart */}
                  {
                    <TagChart
                      readings={chartReadings}
                      unit={tag?.template?.unit || "°C"}
                      tagType={tag?.template?.tag_type}
                      minThreshold={tag?.min_threshold ?? undefined}
                      maxThreshold={tag?.max_threshold ?? undefined}
                      tempUnit={tempUnit}
                      displayTimezone={displayTimezone}
                      onChangeTimezone={onChangeTimezone}
                      sc={sc}
                      t={t}
                    />
                  }

                  {/* Alarm card — visible when the tag has an active alert, alarm history, or out-of-range readings */}
                  {(tag?.alert_active ||
                    tagAlerts.length > 0 ||
                    alarmRanges.length > 0) && (
                    <VisionCard>
                      <Flex align="center" justify="space-between" mb={4}>
                        <HStack gap={3}>
                          <IconBox variant="glass" size="md">
                            <FiAlertTriangle
                              fontSize="20px"
                              color={
                                tag?.alert_active ? "#f56565" : sc.iconColor
                              }
                            />
                          </IconBox>
                          <Heading size="md" color={sc.heading}>
                            {t("tags:detail.alarms")}
                          </Heading>
                        </HStack>
                        {tag?.alert_active && tag?.alert_type && (
                          <Badge colorScheme="red" size="lg">
                            {t(`tags:alertTypes.${tag.alert_type}`, {
                              defaultValue: tag.alert_type,
                            })}
                          </Badge>
                        )}
                      </Flex>

                      {tagAlerts.length === 0 ? (
                        <Text color={sc.textMuted} fontSize="sm">
                          {t("tags:detail.noAlarms")}
                        </Text>
                      ) : (
                        <VStack align="stretch" gap={2}>
                          {tagAlerts.slice(0, 10).map((a: any) => {
                            const isResolved = !!a.resolved_at
                            return (
                              <Box
                                key={a.id}
                                p={3}
                                borderRadius="8px"
                                bg={
                                  isResolved
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(245,101,101,0.10)"
                                }
                                border="1px solid"
                                borderColor={
                                  isResolved
                                    ? "rgba(255,255,255,0.08)"
                                    : "rgba(245,101,101,0.30)"
                                }
                              >
                                <Flex
                                  justify="space-between"
                                  align="start"
                                  wrap="wrap"
                                  gap={2}
                                >
                                  <VStack align="start" gap={1}>
                                    <HStack gap={2}>
                                      <Badge
                                        colorScheme={
                                          isResolved ? "gray" : "red"
                                        }
                                        size="sm"
                                      >
                                        {t(`tags:alertTypes.${a.alert_type}`, {
                                          defaultValue: a.alert_type,
                                        })}
                                      </Badge>
                                      <Badge
                                        colorScheme={
                                          a.severity === "critical"
                                            ? "red"
                                            : "yellow"
                                        }
                                        size="sm"
                                        variant="outline"
                                      >
                                        {a.severity}
                                      </Badge>
                                      {isResolved && (
                                        <Badge
                                          colorScheme="green"
                                          size="sm"
                                          variant="outline"
                                        >
                                          {t("tags:detail.alarmResolved")}
                                        </Badge>
                                      )}
                                    </HStack>
                                    <Text fontSize="xs" color={sc.textMuted}>
                                      {t("tags:detail.alarmTriggeredAt")}:{" "}
                                      {new Date(
                                        a.triggered_at,
                                      ).toLocaleString()}
                                      {isResolved && (
                                        <>
                                          {" "}
                                          • {t("tags:detail.alarmResolvedAt")}:{" "}
                                          {new Date(
                                            a.resolved_at,
                                          ).toLocaleString()}
                                        </>
                                      )}
                                    </Text>
                                    {(a.trigger_value !== null ||
                                      a.threshold_value !== null) && (
                                      <Text fontSize="xs" color={sc.textSubtle}>
                                        {t("tags:detail.alarmValue")}:{" "}
                                        <Text
                                          as="span"
                                          fontWeight="500"
                                          color={sc.heading}
                                        >
                                          {a.trigger_value}
                                        </Text>
                                        {a.threshold_value !== null && (
                                          <>
                                            {" "}
                                            • {t("tags:detail.alarmThreshold")}:{" "}
                                            <Text
                                              as="span"
                                              fontWeight="500"
                                              color={sc.heading}
                                            >
                                              {a.threshold_value}
                                            </Text>
                                          </>
                                        )}
                                      </Text>
                                    )}
                                    {a.notes && (
                                      <Text
                                        fontSize="xs"
                                        color={sc.textMuted}
                                        mt={1}
                                      >
                                        {a.notes}
                                      </Text>
                                    )}
                                  </VStack>
                                </Flex>
                              </Box>
                            )
                          })}
                          {tagAlerts.length > 10 && (
                            <Text
                              fontSize="xs"
                              color={sc.textMuted}
                              textAlign="center"
                            >
                              + {tagAlerts.length - 10} more
                            </Text>
                          )}
                        </VStack>
                      )}

                      {/* Time ranges in which the temperature was out of band */}
                      {alarmRanges.length > 0 && (
                        <Box mt={6}>
                          <Heading size="xs" color={sc.heading} mb={3}>
                            {t(
                              "tags:detail.outOfRangeIntervals",
                              "Out-of-range intervals",
                            )}
                          </Heading>
                          <VStack align="stretch" gap={2}>
                            {alarmRanges.slice(0, 20).map((r, i) => {
                              const mins = Math.round(r.durationMs / 60000)
                              const durationLabel =
                                mins < 60
                                  ? `${mins} min`
                                  : mins < 1440
                                    ? `${(mins / 60).toFixed(1)} h`
                                    : `${(mins / 1440).toFixed(1)} d`
                              return (
                                <Box
                                  key={`${r.start}-${i}`}
                                  p={2}
                                  borderRadius="6px"
                                  bg="rgba(245,101,101,0.06)"
                                  border="1px solid"
                                  borderColor="rgba(245,101,101,0.2)"
                                >
                                  <HStack gap={2} flexWrap="wrap">
                                    <Badge
                                      colorScheme={
                                        r.direction === "over" ? "red" : "blue"
                                      }
                                      size="sm"
                                    >
                                      {r.direction === "over"
                                        ? t("tags:detail.overTemp", "Over")
                                        : t("tags:detail.underTemp", "Under")}
                                    </Badge>
                                    <Text fontSize="xs" color={sc.textMuted}>
                                      {new Date(r.start).toLocaleString()} →{" "}
                                      {new Date(r.end).toLocaleString()}
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color={sc.heading}
                                      fontWeight="500"
                                    >
                                      {durationLabel}
                                    </Text>
                                    <Text fontSize="xs" color={sc.textSubtle}>
                                      {t("tags:detail.alarmPeak", "Peak")}:{" "}
                                      <Text
                                        as="span"
                                        color={sc.heading}
                                        fontWeight="500"
                                      >
                                        {r.peakValue.toFixed(1)}
                                        {tag?.template?.unit ?? ""}
                                      </Text>
                                    </Text>
                                  </HStack>
                                </Box>
                              )
                            })}
                            {alarmRanges.length > 20 && (
                              <Text
                                fontSize="xs"
                                color={sc.textMuted}
                                textAlign="center"
                              >
                                + {alarmRanges.length - 20} more
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      )}
                    </VisionCard>
                  )}

                  {/* Tag Info Result */}
                  {fetchInfoMutation.isSuccess && fetchInfoMutation.data && (
                    <TagInfoResult
                      data={fetchInfoMutation.data}
                      jsonView={infoJsonView}
                      onToggleJson={() => setInfoJsonView(!infoJsonView)}
                      sc={sc}
                      t={t}
                    />
                  )}

                  {/* Read Data Progress */}
                  {readTriggered && (
                    <VisionCard>
                      <Flex justify="space-between" align="center" mb={4}>
                        <HStack gap={3}>
                          <Heading size="md" color={sc.heading}>
                            {t("tags:detail.readProgress")}
                          </Heading>
                          {samplesReceived > 0 && (
                            <Badge colorScheme="green" size="sm">
                              +{samplesReceived.toLocaleString()}
                            </Badge>
                          )}
                        </HStack>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReadTriggered(false)
                            refetchLatestReadings()
                          }}
                        >
                          {t("tags:detail.closeProgress")}
                        </VisionButton>
                      </Flex>
                      <Box
                        mb={4}
                        h="8px"
                        borderRadius="full"
                        bg={sc.borderMuted}
                        overflow="hidden"
                      >
                        <Box
                          h="100%"
                          borderRadius="full"
                          bg={readProgress >= 100 ? "green.400" : "#34a1dd"}
                          w={`${readProgress}%`}
                          transition="width 0.5s ease"
                        />
                      </Box>
                      <HStack justify="space-between" mb={2}>
                        <Text color={sc.textMuted} fontSize="sm">
                          {expectedSamples > 0
                            ? `${samplesReceived.toLocaleString()} / ${expectedSamples.toLocaleString()} ${t("tags:detail.samplesReceived")}`
                            : `${samplesReceived.toLocaleString()} ${t("tags:detail.samplesReceived")}`}
                        </Text>
                        {expectedSamples > 0 && (
                          <Text color={sc.textMuted} fontSize="sm">
                            {readProgress.toFixed(0)}%
                          </Text>
                        )}
                      </HStack>
                      {samplesReceived === 0 && (
                        <HStack gap={2} mt={2}>
                          <Spinner size="xs" color={sc.spinnerColor} />
                          <Text color={sc.textMuted} fontSize="sm">
                            {t("tags:detail.waitingForData")}
                          </Text>
                        </HStack>
                      )}
                      {readProgress >= 100 && (
                        <Text
                          color="green.400"
                          fontSize="sm"
                          fontWeight="500"
                          mt={2}
                        >
                          {t("tags:detail.readComplete")}
                        </Text>
                      )}
                    </VisionCard>
                  )}
                </VStack>
              </ChakraTabs.Content>
            )}

            {showMeasurements && (
              <ChakraTabs.Content value="tag-readings">
                <VisionCard>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="md" color={sc.heading}>
                      {t("tags:detail.recentReadings")}
                    </Heading>
                    <VisionButton
                      variant="ghost"
                      size="sm"
                      onClick={() => refetchLatestReadings()}
                    >
                      <FiRefreshCw /> {t("common:actions.refresh")}
                    </VisionButton>
                  </Flex>
                  <Flex mb={3} align="center" gap={2}>
                    <Text color={sc.textMuted} fontSize="xs">
                      {t("tags:detail.timezone", "Timezone")}
                    </Text>
                    <TimezoneSelector
                      value={displayTimezone}
                      onChange={onChangeTimezone}
                    />
                  </Flex>
                  {readingsLoading ? (
                    <Flex justify="center" py={6}>
                      <Spinner color={sc.spinnerColor} size="sm" />
                    </Flex>
                  ) : latestReadings.length === 0 ? (
                    <Text color={sc.textMuted} fontSize="sm">
                      {t("tags:detail.waitingForData")}
                    </Text>
                  ) : (
                    <>
                      <Box overflowX="auto" maxW="100%">
                        <Table.Root size="sm" style={{ minWidth: "420px" }}>
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader color={sc.textMuted}>
                                {t("tags:detail.timestamp")}
                              </Table.ColumnHeader>
                              <Table.ColumnHeader color={sc.textMuted}>
                                {t("tags:detail.value")}
                              </Table.ColumnHeader>
                              <Table.ColumnHeader color={sc.textMuted}>
                                #
                              </Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {latestReadings.map(
                              (reading: any, index: number) => (
                                <Table.Row key={index}>
                                  <Table.Cell
                                    color={sc.textSecondary}
                                    fontSize="sm"
                                  >
                                    {reading.timestamp
                                      ? formatTimestamp(reading.timestamp)
                                      : "—"}
                                  </Table.Cell>
                                  <Table.Cell
                                    color={sc.heading}
                                    fontWeight="500"
                                    fontSize="sm"
                                  >
                                    {reading.value != null
                                      ? tag?.template?.tag_type ===
                                          "TEMPERATURE" ||
                                        tag?.template?.tag_type ===
                                          "TEMPERATURE_DATALOGGER"
                                        ? formatTemperature(
                                            reading.value,
                                            tempUnit,
                                          )
                                        : `${reading.value.toFixed(2)} ${tag?.template?.unit || ""}`
                                      : "—"}
                                  </Table.Cell>
                                  <Table.Cell
                                    color={sc.textMuted}
                                    fontSize="sm"
                                  >
                                    {(readingsPage - 1) * readingsPageSize +
                                      index +
                                      1}
                                  </Table.Cell>
                                </Table.Row>
                              ),
                            )}
                          </Table.Body>
                        </Table.Root>
                      </Box>
                      {totalReadings > 0 && (
                        <Flex
                          mt={3}
                          justify="space-between"
                          align="center"
                          flexWrap="wrap"
                          gap={2}
                        >
                          <Text color={sc.textMuted} fontSize="xs">
                            {readingsPageSize === 0
                              ? `${totalReadings} ${t("tags:detail.readings")}`
                              : t("tags:detail.readingsRange", {
                                  from:
                                    (readingsPage - 1) * readingsPageSize + 1,
                                  to: Math.min(
                                    readingsPage * readingsPageSize,
                                    totalReadings,
                                  ),
                                  total: totalReadings,
                                })}
                          </Text>
                          <Flex gap={2} align="center">
                            <select
                              value={readingsPageSize}
                              onChange={(e) => {
                                setReadingsPageSize(Number(e.target.value))
                                setReadingsPage(1)
                              }}
                              style={{
                                fontSize: "12px",
                                color: sc.textMuted,
                                background: "transparent",
                                border: `1px solid ${sc.textMuted}`,
                                borderRadius: "6px",
                                padding: "2px 8px",
                                cursor: "pointer",
                              }}
                            >
                              {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                  {size === 0 ? t("common:filter.all") : size}
                                </option>
                              ))}
                            </select>
                            {readingsPageSize !== 0 && (
                              <>
                                <VisionButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setReadingsPage((p) => Math.max(1, p - 1))
                                  }
                                  disabled={readingsPage === 1}
                                >
                                  ←
                                </VisionButton>
                                <VisionButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setReadingsPage((p) => p + 1)}
                                  disabled={readingsPage >= totalPages}
                                >
                                  →
                                </VisionButton>
                              </>
                            )}
                            <VisionButton
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const allReadings = [...chartReadings].sort(
                                  (a: any, b: any) =>
                                    new Date(a.timestamp).getTime() -
                                    new Date(b.timestamp).getTime(),
                                )
                                const csv = [
                                  "timestamp,value",
                                  ...allReadings.map(
                                    (r: any) => `${r.timestamp},${r.value}`,
                                  ),
                                ].join("\n")
                                const blob = new Blob([csv], {
                                  type: "text/csv",
                                })
                                const url = URL.createObjectURL(blob)
                                const a = document.createElement("a")
                                a.href = url
                                a.download = `readings-${tag?.epcid || tagId}.csv`
                                a.click()
                                URL.revokeObjectURL(url)
                              }}
                            >
                              <FiDownload /> CSV
                            </VisionButton>
                          </Flex>
                        </Flex>
                      )}
                    </>
                  )}
                </VisionCard>
              </ChakraTabs.Content>
            )}

            {isTemperatureTag && (
              <ChakraTabs.Content value="instant-temp">
                <ScanTelemetryChart
                  points={instantTempPoints}
                  title={t("tags:detail.tabInstantTemp")}
                  unit="°C"
                  emptyMessage={t("tags:detail.noInstantTempData")}
                  displayTimezone={displayTimezone}
                  onChangeTimezone={onChangeTimezone}
                  formatTimestamp={formatTimestamp}
                  sc={sc}
                  t={t}
                />
              </ChakraTabs.Content>
            )}

            {isDatalogger && (
              <ChakraTabs.Content value="battery">
                <ScanTelemetryChart
                  points={batteryVoltagePoints}
                  title={t("tags:detail.tabBattery")}
                  unit="V"
                  emptyMessage={t("tags:detail.noBatteryData")}
                  displayTimezone={displayTimezone}
                  onChangeTimezone={onChangeTimezone}
                  formatTimestamp={formatTimestamp}
                  yDomain={[2.5, 3.2]}
                  sc={sc}
                  t={t}
                />
              </ChakraTabs.Content>
            )}

            <ChakraTabs.Content value="inventory">
              <VisionCard>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md" color={sc.heading}>
                    {t("tags:detail.tabInventory")}
                  </Heading>
                  <HStack gap={2} align="center">
                    <Text fontSize="xs" color={sc.textMuted}>
                      {t("tags:detail.timezone", "Timezone")}
                    </Text>
                    <TimezoneSelector
                      value={displayTimezone}
                      onChange={onChangeTimezone}
                    />
                  </HStack>
                </Flex>
                {inventoryLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" color={sc.spinnerColor} />
                  </Flex>
                ) : inventoryEvents.length === 0 ? (
                  <Text color={sc.textMuted} fontSize="sm">
                    {t("tags:detail.inventoryEmpty")}
                  </Text>
                ) : (
                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.timestamp")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventoryReader")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventoryRssi")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventoryVoltage")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventoryState")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventorySamples")}
                          </Table.ColumnHeader>
                          <Table.ColumnHeader color={sc.textMuted}>
                            {t("tags:detail.inventoryLocation")}
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {inventoryEvents.map((evt, idx) => {
                          const d = evt.data || ({} as Record<string, any>)
                          const gps = d.gps as
                            | { latitude?: number; longitude?: number }
                            | undefined
                          const lat = gps?.latitude ?? evt.lat
                          const lon = gps?.longitude ?? evt.lon
                          const readerName = evt.reader_id
                            ? readers.find((r) => r.id === evt.reader_id)
                                ?.name || evt.reader_id.slice(0, 8)
                            : "—"
                          return (
                            <Table.Row key={`${evt.timestamp}-${idx}`}>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                                fontFamily="mono"
                              >
                                {formatTimestamp(evt.timestamp)}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                              >
                                {readerName}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                              >
                                {d.rssi != null ? `${d.rssi} dBm` : "—"}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                              >
                                {d.battery_voltage != null
                                  ? `${Number(d.battery_voltage).toFixed(2)} V`
                                  : "—"}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                              >
                                {d.state || "—"}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                              >
                                {d.samples != null ? d.samples : "—"}
                              </Table.Cell>
                              <Table.Cell
                                color={sc.tableCellMuted}
                                fontSize="sm"
                                fontFamily="mono"
                              >
                                {lat != null &&
                                lon != null &&
                                (lat !== 0 || lon !== 0)
                                  ? `${lat.toFixed(4)}, ${lon.toFixed(4)}`
                                  : "—"}
                              </Table.Cell>
                            </Table.Row>
                          )
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                )}
              </VisionCard>
            </ChakraTabs.Content>

            {tag && (
              <ChakraTabs.Content value="details">
                <AssetDetails
                  tag={tag}
                  tpl={tpl}
                  sc={sc}
                  t={t}
                  tagId={tagId!}
                />
              </ChakraTabs.Content>
            )}

            {hasGpsData && (
              <ChakraTabs.Content value="map">
                <TagMapCard
                  points={gpsPoints}
                  formatTimestamp={formatTimestamp}
                  sc={sc}
                  t={t}
                  isActive={activeTab === "map"}
                />
              </ChakraTabs.Content>
            )}
          </ChakraTabs.Root>
        </VStack>

        {/* ══════ RIGHT — SIDEBAR (25%) ══════ */}
        <VStack gap={6} w="100%" minW={0}>
          {/* Tag Information */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("tags:detail.tagInfo")}
            </Heading>
            <VStack align="stretch" gap={3}>
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("tags:detail.epc")}
                </Text>
                <Text color={sc.heading} fontSize="sm" mt={1} fontFamily="mono">
                  {tag.epcid}
                </Text>
              </Box>
              {tag.tid && (
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    TID
                  </Text>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    mt={1}
                    fontFamily="mono"
                  >
                    {tag.tid.slice(0, 32)}
                  </Text>
                </Box>
              )}
              {tag.url && (
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:detail.url")}
                  </Text>
                  <Link
                    href={tag.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color={sc.heading}
                    fontSize="sm"
                    mt={1}
                    fontFamily="mono"
                    wordBreak="break-all"
                  >
                    {tag.url}
                  </Link>
                </Box>
              )}
              {tag.tag_model && (
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:detail.tagModel")}
                  </Text>
                  <Text color={sc.heading} fontSize="sm" mt={1}>
                    {tag.tag_model}
                  </Text>
                </Box>
              )}
              {tag.template && (
                <>
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.template")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.template.name}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.tagType")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.template.tag_type}
                    </Text>
                  </Box>
                  {tag.template.unit && (
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("tags:form.unitLabel")}
                      </Text>
                      <Text color={sc.heading} fontSize="sm" mt={1}>
                        {tag.template.unit}
                      </Text>
                    </Box>
                  )}
                </>
              )}
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("tags:detail.state")}
                </Text>
                <Box mt={1}>
                  <Badge
                    colorScheme={
                      tag.state === "ALERT"
                        ? "red"
                        : tag.state === "ARMED" ||
                            tag.state === "LOGGING" ||
                            tag.state === "READY"
                          ? "green"
                          : tag.state === "FINISHED"
                            ? "purple"
                            : "gray"
                    }
                    size="sm"
                  >
                    {tag.state
                      ? t(`tags:states.${tag.state}`, {
                          defaultValue: tag.state,
                        })
                      : t("tags:detail.unknown")}
                  </Badge>
                </Box>
              </Box>
              {tag.battery_status != null && (
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:detail.battery")}
                  </Text>
                  <Text color={sc.heading} fontSize="sm" mt={1}>
                    {(tag.battery_status / 1000).toFixed(2)} V
                  </Text>
                </Box>
              )}
              {isMeasurementTag && (
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:detail.readings")}
                  </Text>
                  <Text color={sc.heading} fontSize="sm" mt={1}>
                    {totalReadings.toLocaleString()}
                  </Text>
                </Box>
              )}
              <Box>
                <Text
                  fontSize="xs"
                  color={sc.textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {t("tags:detail.lastSeenBy")}
                </Text>
                <Text
                  color={hasLastReader ? sc.heading : sc.textSubtle}
                  fontSize="sm"
                  mt={1}
                >
                  {hasLastReader
                    ? tag.last_seen_by_reader_name || tag.last_seen_by
                    : t("tags:detail.unknown")}
                </Text>
              </Box>
            </VStack>
          </VisionCard>

          {/* Real-time Operations */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={2}>
              {t("tags:detail.realTimeOps")}
            </Heading>
            {!hasLastReader && (
              <Box
                mb={4}
                p={3}
                borderRadius="8px"
                bg="rgba(255, 165, 0, 0.1)"
                border="1px solid rgba(255, 165, 0, 0.3)"
              >
                <Text color="orange.300" fontSize="sm">
                  {t("tags:detail.noReaderWarning")}
                </Text>
              </Box>
            )}
            <VStack gap={4} align="stretch">
              <Box>
                <Text color={sc.textMuted} fontSize="sm" mb={2}>
                  {t("tags:detail.fetchInfoDesc")}
                </Text>
                <VisionButton
                  variant="brand"
                  onClick={() => fetchInfoMutation.mutate()}
                  loading={fetchInfoMutation.isPending}
                  disabled={!hasLastReader}
                  w="100%"
                >
                  <FiRefreshCw /> {t("tags:detail.fetchInfo")}
                </VisionButton>
              </Box>
              <Box>
                <Text color={sc.textMuted} fontSize="sm" mb={2}>
                  {t("tags:detail.readDataDesc")}
                </Text>
                <VisionButton
                  variant="dark"
                  onClick={() => fetchDataMutation.mutate()}
                  loading={fetchDataMutation.isPending}
                  disabled={!hasLastReader}
                  w="100%"
                >
                  <FiActivity /> {t("tags:detail.readData")}
                </VisionButton>
              </Box>
              <Box>
                <Text color={sc.textMuted} fontSize="sm" mb={2}>
                  {t("tags:detail.stopReaderDesc")}
                </Text>
                <VisionButton
                  variant="dark"
                  onClick={() => stopReadMutation.mutate()}
                  loading={stopReadMutation.isPending}
                  disabled={!hasLastReader}
                  w="100%"
                  style={{ borderColor: "rgba(255,100,100,0.4)" }}
                >
                  <FiSquare /> {t("tags:detail.stopReader")}
                </VisionButton>
              </Box>
            </VStack>
          </VisionCard>

          {/* Quick Actions */}
          <VisionCard>
            <Heading size="md" color={sc.heading} mb={4}>
              {t("tags:detail.quickActions")}
            </Heading>
            <VStack gap={2} align="stretch">
              <VisionButton
                variant="dark"
                onClick={() =>
                  navigate({
                    to: "/tag-form",
                    search: { tagId: tag.id },
                  })
                }
                w="100%"
              >
                {t("tags:detail.editTag")}
              </VisionButton>
            </VStack>
          </VisionCard>

          {/* Recent RSSI Activity — for tracking tags */}
          {!isMeasurementTag && (
            <VisionCard>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md" color={sc.heading}>
                  {t("tags:detail.recentDetections")}
                </Heading>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchEvents()}
                >
                  <FiRefreshCw /> {t("common:actions.refresh")}
                </VisionButton>
              </Flex>
              {recentEvents.length === 0 ? (
                <Flex direction="column" align="center" py={6} gap={2}>
                  <FiWifi fontSize="32px" color="gray" />
                  <Text color={sc.textMuted} fontSize="sm">
                    {t("tags:detail.noDetections")}
                  </Text>
                </Flex>
              ) : (
                <VStack align="stretch" gap={2}>
                  {recentEvents.map((event: any, index: number) => (
                    <Box
                      key={index}
                      p={3}
                      borderRadius="8px"
                      bg={sc.eventBg}
                      border="1px solid"
                      borderColor={sc.border}
                    >
                      <HStack justify="space-between">
                        <HStack gap={2}>
                          <FiWifi color="#63b3ed" />
                          <Text color={sc.heading} fontSize="sm">
                            {event.rssi != null
                              ? `${event.rssi} dBm`
                              : t("tags:detail.detected")}
                          </Text>
                        </HStack>
                        <Text color={sc.textMuted} fontSize="xs">
                          {event.timestamp
                            ? format(new Date(event.timestamp), "HH:mm:ss")
                            : ""}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </VisionCard>
          )}
        </VStack>
      </Grid>
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Tag Chart — interactive Recharts with zoom, thresholds          */
/* ------------------------------------------------------------------ */

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { useColorModeValue } from "@/components/ui/color-mode"

function TagChart({
  readings,
  unit,
  tagType,
  minThreshold,
  maxThreshold,
  tempUnit,
  displayTimezone,
  onChangeTimezone,
  sc: _sc,
  t,
}: {
  readings: any[]
  unit: string
  tagType?: string
  minThreshold?: number
  maxThreshold?: number
  tempUnit: import("@/hooks/useTemperatureUnit").TemperatureUnit
  displayTimezone: string
  onChangeTimezone: (tz: string) => void
  sc: any
  t: any
}) {
  // TEMPERATURE_DATALOGGER (OPUS family) reads in °C just like
  // TEMPERATURE — treat them the same for unit + formatting.
  const isTemp =
    tagType === "TEMPERATURE" || tagType === "TEMPERATURE_DATALOGGER"

  // Chart colors adapt to light/dark mode
  const gridColor = useColorModeValue(
    "rgba(0,0,0,0.08)",
    "rgba(255,255,255,0.06)",
  )
  const axisColor = useColorModeValue(
    "rgba(0,0,0,0.3)",
    "rgba(255,255,255,0.25)",
  )
  const tickColor = useColorModeValue("#4A5568", "rgba(255,255,255,0.5)")
  const tooltipBg = useColorModeValue(
    "rgba(255,255,255,0.95)",
    "rgba(15, 21, 44, 0.95)",
  )
  const tooltipBorder = useColorModeValue(
    "rgba(0,0,0,0.1)",
    "rgba(255,255,255,0.15)",
  )
  const tooltipText = useColorModeValue("#1a202c", "#edf3ff")
  const tooltipLabel = useColorModeValue("#718096", "#A0AEC0")
  const headingColor = useColorModeValue("#1a202c", "#edf3ff")
  const mutedColor = useColorModeValue("#718096", "#A0AEC0")
  const trackBg = useColorModeValue(
    "rgba(0,0,0,0.06)",
    "rgba(255,255,255,0.06)",
  )
  const inputBorder = useColorModeValue(
    "rgba(0,0,0,0.15)",
    "rgba(255,255,255,0.15)",
  )

  // Exact brand palette (shared with the Flutter dashboard)
  const BRAND = "#34A1DD"
  const OVER = "#EF4444" // above high threshold → red
  const UNDER = "#C084FC" // below low threshold → purple
  const BAND_COLOR = "rgba(52, 161, 221, 0.15)"

  const displayUnit = isTemp ? "°C" : unit

  // ─── Timezone-aware formatting (shared with the log table) ───────────
  const isLocalTz = displayTimezone === TIMEZONE_LOCAL
  const tzOpt = isLocalTz ? undefined : displayTimezone
  const tzSuffix = displayTimezone === "UTC" ? " UTC" : ""
  const tzParts = useCallback(
    (ms: number) => {
      const parts = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        timeZone: tzOpt,
      }).formatToParts(new Date(ms))
      const get = (type: string) =>
        parts.find((p) => p.type === type)?.value ?? ""
      return {
        y: get("year"),
        mo: get("month"),
        d: get("day"),
        h: get("hour"),
        mi: get("minute"),
        s: get("second"),
      }
    },
    [tzOpt],
  )
  const fmtAxisDate = useCallback(
    (ms: number) => {
      const p = tzParts(ms)
      return `${p.mo}/${p.d}`
    },
    [tzParts],
  )
  const fmtAxisTime = useCallback(
    (ms: number, withSeconds: boolean) => {
      const p = tzParts(ms)
      return withSeconds ? `${p.h}:${p.mi}:${p.s}` : `${p.h}:${p.mi}`
    },
    [tzParts],
  )
  const fmtLogTimestamp = useCallback(
    (ms: number) => {
      const p = tzParts(ms)
      return `${p.y}-${p.mo}-${p.d} ${p.h}:${p.mi}:${p.s}${tzSuffix}`
    },
    [tzParts, tzSuffix],
  )
  const fmtTooltipTimestamp = useCallback(
    (ms: number) => {
      const monthShort = new Intl.DateTimeFormat(undefined, {
        month: "short",
        timeZone: tzOpt,
      }).format(new Date(ms))
      const p = tzParts(ms)
      return `${monthShort} ${Number(p.d)} · ${p.h}:${p.mi}:${p.s}${tzSuffix}`
    },
    [tzParts, tzOpt, tzSuffix],
  )

  // Aggregation mode + range + outlier filter (existing controls kept)
  type AggMode = "raw" | "minute" | "hourly" | "daily"
  const [aggMode, setAggMode] = useState<AggMode>("raw")
  const [filterOutliers, setFilterOutliers] = useState(false)
  // null = "All" (the full 4096-reading load)
  const [rangeLimit, setRangeLimit] = useState<number | null>(null)
  const RANGE_OPTIONS: (number | null)[] = [100, 500, 1000, null]

  // §8 — display-only threshold overrides (never persisted)
  const [overrideMin, setOverrideMin] = useState("")
  const [overrideMax, setOverrideMax] = useState("")
  const parsedMin = overrideMin.trim() === "" ? Number.NaN : Number(overrideMin)
  const parsedMax = overrideMax.trim() === "" ? Number.NaN : Number(overrideMax)
  const lowT = !Number.isNaN(parsedMin)
    ? parsedMin
    : typeof minThreshold === "number"
      ? minThreshold
      : undefined
  const highT = !Number.isNaN(parsedMax)
    ? parsedMax
    : typeof maxThreshold === "number"
      ? maxThreshold
      : undefined

  // Sort raw data by timestamp ascending — values stay in Celsius
  const sortedRaw = [...readings]
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    .map((r) => ({
      time: new Date(r.timestamp).getTime(),
      value: r.value != null ? r.value : null,
    }))

  // IQR-based outlier filter
  const computeIQRBounds = (
    data: { value: number | null }[],
  ): { lower: number; upper: number } | null => {
    const vals = data
      .map((d) => d.value)
      .filter((v) => v != null)
      .sort((a, b) => (a as number) - (b as number)) as number[]
    if (vals.length < 4) return null
    const q1 = vals[Math.floor(vals.length * 0.25)]
    const q3 = vals[Math.floor(vals.length * 0.75)]
    const iqr = q3 - q1
    return { lower: q1 - 1.5 * iqr, upper: q3 + 1.5 * iqr }
  }

  const iqrBounds = filterOutliers ? computeIQRBounds(sortedRaw) : null
  const outlierCount = iqrBounds
    ? sortedRaw.filter(
        (d) =>
          d.value != null &&
          (d.value < iqrBounds.lower || d.value > iqrBounds.upper),
      ).length
    : 0

  const filteredRaw =
    filterOutliers && iqrBounds
      ? sortedRaw.filter(
          (d) =>
            d.value == null ||
            (d.value >= iqrBounds.lower && d.value <= iqrBounds.upper),
        )
      : sortedRaw

  // Apply the range selector (keep the most recent N readings)
  const rawData =
    rangeLimit == null ? filteredRaw : filteredRaw.slice(-rangeLimit)

  // Aggregate data by time bucket
  const aggregate = (
    data: typeof rawData,
    bucketMs: number,
  ): {
    time: number
    avg: number | null
    min: number | null
    max: number | null
    range: [number, number] | null
    count: number
  }[] => {
    const buckets = new Map<
      number,
      { sum: number; min: number; max: number; count: number }
    >()
    for (const d of data) {
      if (d.value == null) continue
      const key = Math.floor(d.time / bucketMs) * bucketMs
      const b = buckets.get(key)
      if (b) {
        b.sum += d.value
        b.min = Math.min(b.min, d.value)
        b.max = Math.max(b.max, d.value)
        b.count++
      } else {
        buckets.set(key, { sum: d.value, min: d.value, max: d.value, count: 1 })
      }
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a - b)
      .map(([time, b]) => ({
        time,
        avg: b.sum / b.count,
        min: b.min,
        max: b.max,
        range: [b.min, b.max] as [number, number],
        count: b.count,
      }))
  }

  const MINUTE_MS = 60_000
  const HOUR_MS = 3600_000
  const DAY_MS = 86400_000
  const AGG_BUCKET: Record<string, number> = {
    minute: MINUTE_MS,
    hourly: HOUR_MS,
    daily: DAY_MS,
  }

  const isAggregated = aggMode !== "raw"
  const baseChartData: any[] =
    aggMode === "raw" ? rawData : aggregate(rawData, AGG_BUCKET[aggMode])

  // Normalise every row to a single plotted value `val`, plus the
  // breach-only `overValue` / `underValue` used by the shaded excursion
  // areas (null when the reading is within band so the fill breaks at
  // threshold crossings).
  const chartData: any[] = baseChartData.map((d) => {
    const val = isAggregated ? d.avg : d.value
    const over = typeof val === "number" && highT != null && val > highT
    const under = typeof val === "number" && lowT != null && val < lowT
    return {
      time: d.time,
      val,
      min: isAggregated ? d.min : undefined,
      max: isAggregated ? d.max : undefined,
      range: isAggregated ? d.range : undefined,
      count: isAggregated ? d.count : 1,
      overValue: over ? val : null,
      underValue: under ? val : null,
    }
  })

  const hasOver = highT != null && chartData.some((d) => d.overValue != null)
  const hasUnder = lowT != null && chartData.some((d) => d.underValue != null)

  // Sampling interval — median gap between consecutive readings
  const samplingSeconds = (() => {
    if (rawData.length < 2) return null
    const gaps: number[] = []
    for (let i = 1; i < rawData.length; i++) {
      const g = rawData[i].time - rawData[i - 1].time
      if (g > 0) gaps.push(g)
    }
    if (gaps.length === 0) return null
    gaps.sort((a, b) => a - b)
    const mid = Math.floor(gaps.length / 2)
    const med = gaps.length % 2 ? gaps[mid] : (gaps[mid - 1] + gaps[mid]) / 2
    return Math.round(med / 1000)
  })()
  const fmtHMS = (sec: number) => {
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    const s = sec % 60
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":")
  }

  // ─── Zoom / pan window ───────────────────────────────────────────────
  const [domainX, setDomainX] = useState<[number, number] | null>(null)
  const [boxSelect, setBoxSelect] = useState(false)
  const [refLeft, setRefLeft] = useState<number | null>(null)
  const [refRight, setRefRight] = useState<number | null>(null)
  const downPxRef = useRef<number | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)

  const fullStart = chartData[0]?.time ?? 0
  const fullEnd = chartData[chartData.length - 1]?.time ?? 0
  const activeXDomain: [number, number] = domainX || [fullStart, fullEnd]
  const visibleSpan = activeXDomain[1] - activeXDomain[0]
  const showSeconds = visibleSpan > 0 && visibleSpan < 10 * 60 * 1000

  const visibleRows = chartData.filter(
    (d) => d.time >= activeXDomain[0] && d.time <= activeXDomain[1],
  )
  const visibleCount = visibleRows.length

  // Threshold-centred Y domain with 0.5° tick snapping (recomputed over the
  // currently visible window).
  const computeY = (rows: any[]) => {
    const vals: number[] = []
    for (const r of rows) {
      if (isAggregated) {
        if (r.min != null) vals.push(r.min)
        if (r.max != null) vals.push(r.max)
      } else if (r.val != null) vals.push(r.val)
    }
    const dataMin = vals.length ? Math.min(...vals) : undefined
    const dataMax = vals.length ? Math.max(...vals) : undefined
    const hasBand = lowT != null && highT != null && highT > lowT
    let yMin: number
    let yMax: number
    if (hasBand) {
      const band = (highT as number) - (lowT as number)
      const margin = 0.25 * band
      yMin = Math.min(
        (lowT as number) - margin,
        dataMin ?? (lowT as number) - margin,
      )
      yMax = Math.max(
        (highT as number) + margin,
        dataMax ?? (highT as number) + margin,
      )
    } else if (dataMin != null && dataMax != null) {
      const pad = (dataMax - dataMin || 1) * 0.1
      yMin = dataMin - pad
      yMax = dataMax + pad
    } else {
      return { min: 0, max: 1, ticks: [0, 1] }
    }
    const rawStep = (yMax - yMin) / 8
    const step = rawStep <= 0.5 ? 0.5 : Math.ceil(rawStep / 0.5) * 0.5
    const snappedMin = Math.floor(yMin / step) * step
    const snappedMax = Math.ceil(yMax / step) * step
    const ticks: number[] = []
    for (let v = snappedMin; v <= snappedMax + step * 0.001; v += step) {
      ticks.push(Math.round(v * 100) / 100)
    }
    return { min: snappedMin, max: snappedMax, ticks }
  }

  const yInfo = computeY(visibleRows.length ? visibleRows : chartData)
  const isZoomed = domainX !== null

  // Box-select handlers (only active while armed)
  const onChartDown = (e: any) => {
    if (!boxSelect) return
    if (e?.activeLabel != null) {
      setRefLeft(e.activeLabel)
      setRefRight(e.activeLabel)
      downPxRef.current = typeof e.chartX === "number" ? e.chartX : null
    }
  }
  const onChartMove = (e: any) => {
    if (!boxSelect || refLeft == null) return
    if (e?.activeLabel != null) setRefRight(e.activeLabel)
  }
  const onChartUp = (e: any) => {
    if (!boxSelect || refLeft == null || refRight == null) {
      setRefLeft(null)
      setRefRight(null)
      return
    }
    const upPx = typeof e?.chartX === "number" ? e.chartX : null
    const pxDist =
      downPxRef.current != null && upPx != null
        ? Math.abs(upPx - downPxRef.current)
        : 999
    if (refLeft !== refRight && pxDist >= 8) {
      setDomainX([Math.min(refLeft, refRight), Math.max(refLeft, refRight)])
    }
    setRefLeft(null)
    setRefRight(null)
    downPxRef.current = null
  }

  const resetZoom = () => setDomainX(null)

  // Pan scrollbar — slides the fixed-width window, preserving resolution
  const fullSpan = fullEnd - fullStart || 1
  const thumbFrac = Math.min(1, Math.max(0.02, visibleSpan / fullSpan))
  const thumbLeftFrac = Math.max(
    0,
    Math.min(1 - thumbFrac, (activeXDomain[0] - fullStart) / fullSpan),
  )
  const slideTo = (leftFrac: number) => {
    const clamped = Math.max(0, Math.min(1 - thumbFrac, leftFrac))
    const ns = fullStart + clamped * fullSpan
    setDomainX([ns, ns + visibleSpan])
  }
  const startThumbDrag = (e: ReactMouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const track = trackRef.current
    if (!track) return
    const trackW = track.getBoundingClientRect().width || 1
    const startX = e.clientX
    const startLeft = thumbLeftFrac
    const onMove = (ev: MouseEvent) =>
      slideTo(startLeft + (ev.clientX - startX) / trackW)
    const onUp = () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }
  const jumpTrack = (e: ReactMouseEvent) => {
    const track = trackRef.current
    if (!track) return
    const rect = track.getBoundingClientRect()
    const clickFrac = (e.clientX - rect.left) / (rect.width || 1)
    slideTo(clickFrac - thumbFrac / 2)
  }

  // ─── Temperature log table (§10) ─────────────────────────────────────
  const LOG_PAGE_SIZES = [100, 200, 500, 1000, 2000, 3000, 0] // 0 = All
  const [logPageSize, setLogPageSize] = useState(100)
  const [logPage, setLogPage] = useState(1)
  const logRows = [...readings].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )
  const logTotal = logRows.length
  // Reset to page 1 when the page size or dataset size changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the change triggers, not effect-body reads
  useEffect(() => {
    setLogPage(1)
  }, [logPageSize, logTotal])
  const effPageSize = logPageSize === 0 ? logTotal || 1 : logPageSize
  const logPages = Math.max(1, Math.ceil(logTotal / effPageSize))
  const safePage = Math.min(logPage, logPages)
  const logStart = (safePage - 1) * effPageSize
  const pageRows = logRows.slice(logStart, logStart + effPageSize)
  const statusOf = (v: number | null | undefined) => {
    if (typeof v !== "number") return null
    if (highT != null && v > highT)
      return { color: OVER, label: t("tags:detail.aboveHigh") }
    if (lowT != null && v < lowT)
      return { color: UNDER, label: t("tags:detail.belowLow") }
    return { color: BRAND, label: t("tags:detail.inRange") }
  }
  const fmtValue = (v: number | null | undefined) =>
    typeof v === "number"
      ? isTemp
        ? formatTemperature(v, tempUnit)
        : `${v.toFixed(2)} ${unit}`
      : "—"

  if (chartData.length === 0) {
    return (
      <VisionCard mb={6}>
        <Heading size="md" color={headingColor} mb={4}>
          {t("tags:detail.measurements")}
        </Heading>
        <Text color={mutedColor}>{t("tags:detail.waitingForData")}</Text>
      </VisionCard>
    )
  }

  const legendDot = (color: string) => (
    <Box
      as="span"
      display="inline-block"
      w="8px"
      h="8px"
      borderRadius="full"
      bg={color}
      mr="4px"
    />
  )

  const TooltipContent = ({ active, payload }: any) => {
    if (boxSelect || !active || !payload?.length) return null
    const row = payload[0]?.payload
    if (!row || row.val == null) return null
    const v = row.val as number
    const over = highT != null && v > highT
    const under = lowT != null && v < lowT
    return (
      <div
        style={{
          background: tooltipBg,
          border: `1px solid ${tooltipBorder}`,
          borderRadius: "8px",
          padding: "8px 10px",
          fontSize: "12px",
        }}
      >
        <div style={{ color: tooltipLabel, marginBottom: "2px" }}>
          {fmtTooltipTimestamp(row.time)}
        </div>
        <div
          style={{
            color: over ? OVER : under ? UNDER : tooltipText,
            fontWeight: 600,
          }}
        >
          {fmtValue(v)}
          {over
            ? ` · ${t("tags:detail.aboveHigh")}`
            : under
              ? ` · ${t("tags:detail.belowLow")}`
              : ""}
        </div>
      </div>
    )
  }

  const renderXTick = (props: any) => {
    const { x, y, payload } = props
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={11}
          textAnchor="middle"
          fontSize={10}
          fill={tickColor}
        >
          {fmtAxisDate(payload.value)}
        </text>
        <text
          x={0}
          y={0}
          dy={23}
          textAnchor="middle"
          fontSize={10}
          fill={tickColor}
        >
          {fmtAxisTime(payload.value, showSeconds)}
        </text>
      </g>
    )
  }

  const renderDot = (props: any) => {
    const { cx, cy, payload, key } = props
    if (cx == null || cy == null || payload?.val == null) return <g key={key} />
    const v = payload.val as number
    const over = highT != null && v > highT
    const under = lowT != null && v < lowT
    const breach = over || under
    if (!breach && visibleCount > 200) return <g key={key} />
    const color = over ? OVER : under ? UNDER : BRAND
    return (
      <circle
        key={key}
        cx={cx}
        cy={cy}
        r={breach ? 2.2 : 1.4}
        fill={color}
        stroke={breach ? "#ffffff" : "none"}
        strokeWidth={breach ? 0.8 : 0}
      />
    )
  }

  return (
    <>
      <VisionCard mb={6}>
        <Flex justify="space-between" align="flex-start" mb={2}>
          <Box>
            <HStack gap={3}>
              <Heading size="md" color={headingColor}>
                {t("tags:detail.measurements")}
              </Heading>
              <Badge colorScheme="blue" size="sm">
                {isAggregated
                  ? `${chartData.length} ${aggMode === "minute" ? "min" : aggMode === "hourly" ? "h" : "d"} (${rawData.length} pts)`
                  : `${chartData.length.toLocaleString()} points`}
              </Badge>
            </HStack>
            {isZoomed && (
              <Badge
                mt={1}
                size="sm"
                colorScheme="blue"
                variant="subtle"
                cursor="pointer"
                onClick={resetZoom}
              >
                {t("tags:detail.viewingKofN", {
                  k: visibleCount.toLocaleString(),
                  n: chartData.length.toLocaleString(),
                })}{" "}
                ✕
              </Badge>
            )}
          </Box>
          <HStack gap={2}>
            <VisionButton
              variant={boxSelect ? "brand" : "ghost"}
              size="sm"
              onClick={() => {
                setBoxSelect((b) => !b)
                setRefLeft(null)
                setRefRight(null)
              }}
            >
              <FiSquare /> {t("tags:detail.boxSelect")}
            </VisionButton>
            {isZoomed && (
              <VisionButton variant="ghost" size="sm" onClick={resetZoom}>
                {t("tags:detail.resetZoom")}
              </VisionButton>
            )}
          </HStack>
        </Flex>

        {/* Range pills + sampling interval + threshold overrides */}
        <Flex gap={2} mb={3} align="center" wrap="wrap">
          <HStack gap={1}>
            {RANGE_OPTIONS.map((opt) => (
              <Badge
                key={opt ?? "all"}
                cursor="pointer"
                variant={rangeLimit === opt ? "solid" : "outline"}
                colorScheme={rangeLimit === opt ? "blue" : "gray"}
                onClick={() => {
                  setRangeLimit(opt)
                  resetZoom()
                }}
                size="sm"
              >
                {opt == null
                  ? t("tags:detail.rangeAll")
                  : t("tags:detail.rangeLast", { n: opt })}
              </Badge>
            ))}
          </HStack>

          {samplingSeconds != null && (
            <Text color={mutedColor} fontSize="xs">
              {t("tags:detail.samplingInterval")} {fmtHMS(samplingSeconds)}
            </Text>
          )}

          <HStack gap={1} ml="auto">
            <input
              type="number"
              step="0.5"
              value={overrideMin}
              placeholder={
                typeof minThreshold === "number"
                  ? String(minThreshold)
                  : t("tags:detail.minOverride")
              }
              onChange={(e) => setOverrideMin(e.target.value)}
              style={{
                width: "78px",
                fontSize: "12px",
                color: headingColor,
                background: "transparent",
                border: `1px solid ${inputBorder}`,
                borderRadius: "6px",
                padding: "3px 6px",
              }}
            />
            <input
              type="number"
              step="0.5"
              value={overrideMax}
              placeholder={
                typeof maxThreshold === "number"
                  ? String(maxThreshold)
                  : t("tags:detail.maxOverride")
              }
              onChange={(e) => setOverrideMax(e.target.value)}
              style={{
                width: "78px",
                fontSize: "12px",
                color: headingColor,
                background: "transparent",
                border: `1px solid ${inputBorder}`,
                borderRadius: "6px",
                padding: "3px 6px",
              }}
            />
          </HStack>
        </Flex>

        {/* Aggregation mode + outlier filter */}
        <HStack gap={2} mb={3} wrap="wrap">
          {(["raw", "minute", "hourly", "daily"] as const).map((mode) => (
            <Badge
              key={mode}
              cursor="pointer"
              variant={aggMode === mode ? "solid" : "outline"}
              colorScheme={aggMode === mode ? "blue" : "gray"}
              onClick={() => {
                setAggMode(mode)
                resetZoom()
              }}
              size="sm"
            >
              {t(`tags:detail.agg_${mode}`)}
            </Badge>
          ))}
          <Box w="1px" h="16px" bg={mutedColor} opacity={0.3} />
          <Badge
            cursor="pointer"
            variant={filterOutliers ? "solid" : "outline"}
            colorScheme={filterOutliers ? "orange" : "gray"}
            onClick={() => setFilterOutliers(!filterOutliers)}
            size="sm"
          >
            {t("tags:detail.filterOutliers")}
            {filterOutliers && outlierCount > 0 && ` (−${outlierCount})`}
          </Badge>
        </HStack>

        {/* Legend */}
        <HStack gap={4} mb={2} wrap="wrap" fontSize="xs" color={mutedColor}>
          <Flex align="center">
            {legendDot(BRAND)}
            {t("tags:detail.inRange")}
          </Flex>
          {highT != null && (
            <Flex align="center">
              {legendDot(OVER)}
              {t("tags:detail.aboveHigh")} ({highT}
              {displayUnit})
            </Flex>
          )}
          {lowT != null && (
            <Flex align="center">
              {legendDot(UNDER)}
              {t("tags:detail.belowLow")} ({lowT}
              {displayUnit})
            </Flex>
          )}
        </HStack>

        <Box
          h="400px"
          userSelect="none"
          cursor={boxSelect ? "crosshair" : "default"}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 6 }}
              onMouseDown={onChartDown}
              onMouseMove={onChartMove}
              onMouseUp={onChartUp}
            >
              <defs>
                <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={BRAND} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="time"
                type="number"
                domain={activeXDomain}
                allowDataOverflow
                stroke={axisColor}
                height={36}
                tick={renderXTick}
              />
              <YAxis
                domain={[yInfo.min, yInfo.max]}
                ticks={yInfo.ticks}
                allowDataOverflow
                stroke={axisColor}
                fontSize={11}
                tick={{ fill: tickColor }}
                tickFormatter={(v: number) =>
                  v === yInfo.min || v === yInfo.max ? "" : `${v}${displayUnit}`
                }
                width={64}
              />
              {!boxSelect && <Tooltip content={<TooltipContent />} />}

              {/* Faint brand base area under the full curve */}
              <Area
                type="monotone"
                dataKey="val"
                stroke="transparent"
                fill="url(#valueGradient)"
                connectNulls
                isAnimationActive={false}
              />

              {/* Aggregated min/max band */}
              {isAggregated && (
                <Area
                  type="monotone"
                  dataKey="range"
                  stroke="transparent"
                  fill={BAND_COLOR}
                  connectNulls
                  isAnimationActive={false}
                />
              )}

              {/* Over-excursion fill: curve → high threshold (red) */}
              {hasOver && (
                <Area
                  type="monotone"
                  dataKey="overValue"
                  baseValue={highT}
                  stroke="none"
                  fill={OVER}
                  fillOpacity={0.28}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
              {/* Under-excursion fill: curve → low threshold (purple) */}
              {hasUnder && (
                <Area
                  type="monotone"
                  dataKey="underValue"
                  baseValue={lowT}
                  stroke="none"
                  fill={UNDER}
                  fillOpacity={0.26}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}

              {/* Dashed threshold guide lines */}
              {highT != null && (
                <ReferenceLine
                  y={highT}
                  stroke={OVER}
                  strokeOpacity={0.7}
                  strokeDasharray="8 4"
                  label={{
                    value: `${t("tags:detail.aboveHigh")} ${highT}${displayUnit}`,
                    fill: OVER,
                    fontSize: 11,
                    position: "insideTopLeft",
                  }}
                />
              )}
              {lowT != null && (
                <ReferenceLine
                  y={lowT}
                  stroke={UNDER}
                  strokeOpacity={0.7}
                  strokeDasharray="8 4"
                  label={{
                    value: `${t("tags:detail.belowLow")} ${lowT}${displayUnit}`,
                    fill: UNDER,
                    fontSize: 11,
                    position: "insideBottomLeft",
                  }}
                />
              )}

              {/* Aggregated min/max guides */}
              {isAggregated && (
                <>
                  <Line
                    type="monotone"
                    dataKey="min"
                    stroke="rgba(52, 161, 221, 0.4)"
                    strokeWidth={1}
                    strokeDasharray="4 2"
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="max"
                    stroke="rgba(52, 161, 221, 0.4)"
                    strokeWidth={1}
                    strokeDasharray="4 2"
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Primary value line + state-coloured dots */}
              <Line
                type="monotone"
                dataKey="val"
                stroke={BRAND}
                strokeWidth={2.5}
                dot={renderDot}
                activeDot={{ r: 3, fill: BRAND }}
                connectNulls
                isAnimationActive={false}
              />

              {boxSelect && refLeft != null && refRight != null && (
                <ReferenceArea
                  x1={refLeft}
                  x2={refRight}
                  strokeOpacity={0.3}
                  fill="rgba(52, 161, 221, 0.2)"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>

        {/* Pan scrollbar — keeps the zoom resolution while sliding */}
        {isZoomed && (
          <Box
            ref={trackRef}
            mt={2}
            h="12px"
            borderRadius="full"
            bg={trackBg}
            position="relative"
            cursor="pointer"
            onMouseDown={jumpTrack}
          >
            <Box
              position="absolute"
              top="0"
              h="100%"
              borderRadius="full"
              bg={BRAND}
              opacity={0.6}
              left={`${thumbLeftFrac * 100}%`}
              width={`${thumbFrac * 100}%`}
              cursor="grab"
              onMouseDown={startThumbDrag}
            />
          </Box>
        )}
      </VisionCard>

      {/* ─── Temperature log table (§10) ─────────────────────────────── */}
      <VisionCard mb={6}>
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
          <Heading size="md" color={headingColor}>
            {t("tags:detail.temperatureLog")}
          </Heading>
          <HStack gap={2} align="center">
            <Text fontSize="xs" color={mutedColor}>
              {t("tags:detail.timezone", "Timezone")}
            </Text>
            <TimezoneSelector
              value={displayTimezone}
              onChange={onChangeTimezone}
            />
          </HStack>
        </Flex>

        {logTotal === 0 ? (
          <Text color={mutedColor} fontSize="sm">
            {t("tags:detail.waitingForData")}
          </Text>
        ) : (
          <>
            <Box overflowX="auto" maxW="100%">
              <Table.Root size="sm" style={{ minWidth: "480px" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color={mutedColor}>
                      #
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={mutedColor}>
                      {t("tags:detail.timestamp")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={mutedColor}>
                      {t("tags:detail.value")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader color={mutedColor}>
                      {t("tags:detail.status")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pageRows.map((r: any, index: number) => {
                    const st = statusOf(r.value)
                    return (
                      <Table.Row key={`${r.timestamp}-${index}`}>
                        <Table.Cell color={mutedColor} fontSize="sm">
                          {logStart + index + 1}
                        </Table.Cell>
                        <Table.Cell
                          color={tooltipText}
                          fontSize="sm"
                          fontFamily="mono"
                        >
                          {r.timestamp
                            ? fmtLogTimestamp(new Date(r.timestamp).getTime())
                            : "—"}
                        </Table.Cell>
                        <Table.Cell
                          color={headingColor}
                          fontWeight="500"
                          fontSize="sm"
                        >
                          {fmtValue(r.value)}
                        </Table.Cell>
                        <Table.Cell fontSize="sm">
                          {st && (
                            <Flex align="center" color={mutedColor}>
                              {legendDot(st.color)}
                              {st.label}
                            </Flex>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Root>
            </Box>

            <Flex
              mt={3}
              justify="space-between"
              align="center"
              wrap="wrap"
              gap={2}
            >
              <Text color={mutedColor} fontSize="xs">
                {t("tags:detail.showingRange", {
                  from: logStart + 1,
                  to: Math.min(logStart + effPageSize, logTotal),
                  total: logTotal,
                })}
              </Text>
              <HStack gap={2} align="center">
                <Text color={mutedColor} fontSize="xs">
                  {t("tags:detail.rowsPerPage")}
                </Text>
                <select
                  value={logPageSize}
                  onChange={(e) => setLogPageSize(Number(e.target.value))}
                  style={{
                    fontSize: "12px",
                    color: mutedColor,
                    background: "transparent",
                    border: `1px solid ${inputBorder}`,
                    borderRadius: "6px",
                    padding: "2px 8px",
                    cursor: "pointer",
                  }}
                >
                  {LOG_PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size === 0 ? t("tags:detail.rangeAll") : size}
                    </option>
                  ))}
                </select>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage(1)}
                  disabled={safePage === 1}
                >
                  «
                </VisionButton>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                >
                  ←
                </VisionButton>
                <Text color={mutedColor} fontSize="xs">
                  {t("tags:detail.pageXofY", { x: safePage, y: logPages })}
                </Text>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage((p) => Math.min(logPages, p + 1))}
                  disabled={safePage >= logPages}
                >
                  →
                </VisionButton>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage(logPages)}
                  disabled={safePage >= logPages}
                >
                  »
                </VisionButton>
              </HStack>
            </Flex>
          </>
        )}
      </VisionCard>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* ScanTelemetryChart — simple line over per-scan TagEvent data    */
/* ------------------------------------------------------------------ */

function ScanTelemetryChart({
  points,
  title,
  unit,
  emptyMessage,
  displayTimezone,
  onChangeTimezone,
  formatTimestamp,
  yDomain,
  sc,
  t,
}: {
  points: { timestamp: string; value: number }[]
  title: string
  unit: string
  emptyMessage: string
  displayTimezone: string
  onChangeTimezone: (tz: string) => void
  formatTimestamp: (ts: string) => string
  yDomain?: [number, number]
  sc: any
  t: any
}) {
  const chartData = points.map((p) => ({
    time: new Date(p.timestamp).getTime(),
    value: p.value,
  }))
  const gridColor = useColorModeValue(
    "rgba(0,0,0,0.08)",
    "rgba(255,255,255,0.06)",
  )
  const axisColor = useColorModeValue(
    "rgba(0,0,0,0.3)",
    "rgba(255,255,255,0.25)",
  )
  const tickColor = useColorModeValue("#4a5568", "#A0AEC0")
  const BRAND = "#34a1dd"

  if (chartData.length === 0) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {title}
        </Heading>
        <Text color={sc.textMuted}>{emptyMessage}</Text>
      </VisionCard>
    )
  }

  return (
    <VisionCard>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" color={sc.heading}>
          {title}
        </Heading>
        <HStack gap={3} align="center">
          <Text fontSize="xs" color={sc.textMuted}>
            {t("tags:detail.timezone", "Timezone")}
          </Text>
          <TimezoneSelector
            value={displayTimezone}
            onChange={onChangeTimezone}
          />
          <Badge colorScheme="blue" size="sm">
            {chartData.length.toLocaleString()} points
          </Badge>
        </HStack>
      </Flex>
      <Box h="320px">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="time"
              type="number"
              domain={["auto", "auto"]}
              tickFormatter={(ts) =>
                formatInTimezone(new Date(ts).toISOString(), displayTimezone, {
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }
              stroke={axisColor}
              fontSize={11}
              tick={{ fill: tickColor }}
            />
            <YAxis
              stroke={axisColor}
              fontSize={11}
              tick={{ fill: tickColor }}
              tickFormatter={(v) => `${v}${unit}`}
              width={70}
              domain={yDomain ?? ["auto", "auto"]}
            />
            <Tooltip
              contentStyle={{
                background: "rgba(15, 21, 44, 0.95)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px",
                color: "#edf3ff",
              }}
              labelFormatter={(ts) =>
                formatTimestamp(new Date(ts as number).toISOString())
              }
              formatter={(v: any) => [`${v}${unit}`, ""]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={BRAND}
              strokeWidth={2}
              dot={chartData.length < 100 ? { r: 3, fill: BRAND } : false}
              activeDot={{ r: 4, fill: BRAND }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Log table beneath the chart — most recent first, capped at
          200 rows so a long-running tag doesn't blow up the DOM. */}
      <Box mt={6}>
        <Text
          fontSize="xs"
          color={sc.textMuted}
          textTransform="uppercase"
          letterSpacing="wider"
          mb={2}
        >
          {t("tags:detail.scanTelemetryLog")}
        </Text>
        <Box overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("tags:detail.colTimestamp")}
                </Table.ColumnHeader>
                <Table.ColumnHeader color={sc.textMuted}>
                  {t("tags:detail.colValue")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {[...points]
                .reverse()
                .slice(0, 200)
                .map((p, idx) => (
                  <Table.Row key={`${p.timestamp}-${idx}`}>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {formatTimestamp(p.timestamp)}
                    </Table.Cell>
                    <Table.Cell
                      color={sc.heading}
                      fontSize="sm"
                      fontFamily="mono"
                    >
                      {p.value.toFixed(2)}
                      {unit}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table.Root>
        </Box>
        {points.length > 200 && (
          <Text fontSize="xs" color={sc.textMuted} mt={2}>
            {t("tags:detail.scanTelemetryLogTruncated", {
              shown: 200,
              total: points.length,
            })}
          </Text>
        )}
      </Box>
    </VisionCard>
  )
}

/* ------------------------------------------------------------------ */
/* Tag Info Result — formatted table with JSON toggle              */
/* ------------------------------------------------------------------ */

function TagInfoResult({
  data,
  jsonView,
  onToggleJson,
  sc,
  t,
}: {
  data: any[]
  jsonView: boolean
  onToggleJson: () => void
  sc: any
  t: any
}) {
  // Extract first successful result
  const info = data.find((r: any) => r.status === "SUCCESS")
  const d = info?.data

  if (!d) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("tags:detail.tagInfo")}
        </Heading>
        <Text color={sc.textMuted} fontSize="sm">
          {t("tags:detail.tagNotFound")}
        </Text>
      </VisionCard>
    )
  }

  const stateColors: Record<string, string> = {
    LOGGING: "green",
    ARMED: "blue",
    READY: "teal",
    FINISHED: "purple",
    SLEEP: "gray",
    INVALID: "red",
  }

  const fields = [
    { label: "EPC", value: d.epc },
    { label: t("tags:detail.chipType"), value: d.chip_type },
    { label: "TID", value: d.tid ? d.tid.slice(0, 32) : null },
    {
      label: t("tags:detail.state"),
      value: d.state,
      badge: true,
      color: stateColors[d.state] || "gray",
    },
    { label: "RSSI", value: d.rssi != null ? `${d.rssi} dBm` : null },
    {
      label: t("tags:detail.samples"),
      value:
        d.total_samples != null && d.samples != null
          ? `${d.total_samples.toLocaleString()} / ${d.samples.toLocaleString()}`
          : d.samples != null
            ? d.samples.toLocaleString()
            : null,
    },
    {
      label: t("tags:detail.lastSeen"),
      value: d.time_last_seen
        ? new Date(d.time_last_seen * 1000).toLocaleString()
        : null,
    },
  ]

  const gps = d.gps

  return (
    <VisionCard>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" color={sc.heading}>
          {t("tags:detail.fetchInfoResult")}
        </Heading>
        <VisionButton variant="ghost" size="sm" onClick={onToggleJson}>
          <FiCode /> {jsonView ? t("tags:detail.tableView") : "JSON"}
        </VisionButton>
      </Flex>

      {jsonView ? (
        <Box
          p={3}
          borderRadius="8px"
          bg={sc.eventBg}
          border="1px solid"
          borderColor={sc.border}
        >
          <Text
            color={sc.heading}
            fontSize="xs"
            fontFamily="mono"
            whiteSpace="pre-wrap"
          >
            {JSON.stringify(data, null, 2)}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" gap={0}>
          <Box overflowX="auto" maxW="100%">
            <Table.Root size="sm">
              <Table.Body>
                {fields
                  .filter((f) => f.value != null)
                  .map((f) => (
                    <Table.Row key={f.label}>
                      <Table.Cell
                        color={sc.textMuted}
                        fontSize="xs"
                        textTransform="uppercase"
                        fontWeight="bold"
                        w="180px"
                      >
                        {f.label}
                      </Table.Cell>
                      <Table.Cell
                        color={sc.heading}
                        fontSize="sm"
                        fontFamily="mono"
                      >
                        {f.badge ? (
                          <Badge
                            colorScheme={(f.color || "gray") as any}
                            size="sm"
                          >
                            {f.value}
                          </Badge>
                        ) : (
                          f.value
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                {gps && (
                  <Table.Row>
                    <Table.Cell
                      color={sc.textMuted}
                      fontSize="xs"
                      textTransform="uppercase"
                      fontWeight="bold"
                      w="180px"
                    >
                      <HStack gap={1}>
                        <FiMapPin size={12} />
                        <Text>GPS</Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell
                      color={sc.heading}
                      fontSize="sm"
                      fontFamily="mono"
                    >
                      {gps.latitude?.toFixed(6)}, {gps.longitude?.toFixed(6)}
                      {gps.altitude != null && (
                        <Text as="span" color={sc.textMuted} ml={2}>
                          ({gps.altitude.toFixed(1)}m)
                        </Text>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      )}
    </VisionCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Tag Map — Leaflet map showing inventory GPS positions              */
/* ═══════════════════════════════════════════════════════════════════════ */

import "leaflet/dist/leaflet.css"
import L from "leaflet"
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet"

// Fix Leaflet default marker icons (broken in bundlers).
// Setting _getIconUrl = undefined leaves the property defined-but-undefined,
// so Leaflet's `this._getIconUrl(name)` call resolves to `undefined(name)` and
// throws "this._getIconUrl is not a function" the moment the first Marker
// mounts. `delete` removes the override entirely so the prototype lookup
// falls through to L.Icon.prototype._getIconUrl, which reads from
// options[name + 'Url'] — exactly what mergeOptions() populates below.
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
// `delete` (not = undefined) is required: setting to undefined leaves the override in place and Leaflet calls it as a function at runtime.
// biome-ignore lint/performance/noDelete: see comment above
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

interface GpsPoint {
  lat: number
  lon: number
  timestamp: string
  data?: Record<string, any> | null
}

// Mounted inside <MapContainer> so it can grab the Leaflet map via useMap().
// Used to (a) call invalidateSize() once the container's final size is known
// and again whenever the map tab becomes visible (Chakra <Tabs.Content> keeps
// the panel mounted across tab switches but toggles `hidden`, so Leaflet's
// cached container size goes stale while the map is hidden), and (b) expose
// a refresh() handle to the parent so the "Refresh Map" button can recenter
// without remounting the map — remounting <MapContainer> under react-leaflet
// v4 + React 18 races with Leaflet's internal event cleanup and throws
// "Cannot read properties of undefined (reading '_leaflet_events')".
function MapController({
  points,
  isActive,
  refreshRef,
  showAllPins,
}: {
  points: GpsPoint[]
  isActive: boolean
  refreshRef: React.MutableRefObject<(() => void) | null>
  showAllPins: boolean
}) {
  const map = useMap()

  // Fit all locations into view when "All locations" is on; otherwise
  // recenter on the latest position.
  const recenter = useCallback(() => {
    if (points.length === 0) return
    if (showAllPins && points.length > 1) {
      const bounds = L.latLngBounds(
        points.map((p) => [p.lat, p.lon] as [number, number]),
      )
      map.fitBounds(bounds, { padding: [40, 40] })
    } else {
      map.setView([points[0].lat, points[0].lon], map.getZoom())
    }
  }, [map, points, showAllPins])

  // Recompute size + re-zoom when the panel becomes active or the view
  // mode (latest vs all) flips — recenter is a dep so toggling re-zooms.
  useEffect(() => {
    if (!isActive) return
    const timer = setTimeout(() => {
      map.invalidateSize()
      recenter()
    }, 200)
    return () => clearTimeout(timer)
  }, [map, isActive, recenter])

  // Expose recenter to the parent's "Refresh Map" button.
  useEffect(() => {
    refreshRef.current = recenter
    return () => {
      refreshRef.current = null
    }
  }, [refreshRef, recenter])

  return null
}

function TagMapCard({
  points,
  formatTimestamp,
  sc,
  t,
  isActive,
}: {
  points: GpsPoint[]
  formatTimestamp: (ts: string) => string
  sc: Record<string, string>
  t: (key: string) => string
  isActive: boolean
}) {
  const refreshRef = useRef<(() => void) | null>(null)

  // Chakra's <Tabs.Content> keeps panels mounted across tab switches but
  // toggles the `hidden` attribute. Leaflet's MapContainer throws during
  // init when its parent is `display: none` (it can't measure the container,
  // and an internal pane lookup returns undefined). Without an error boundary
  // above tag-detail, that throw kills the whole layout for any tag with GPS
  // data. Defer mounting TagMap until the user first opens the map tab, when
  // the container is actually visible. Once shown, keep it mounted forever
  // so we never re-trigger the underlying react-leaflet v4 cleanup race.
  const [hasBeenActive, setHasBeenActive] = useState(isActive)
  useEffect(() => {
    if (isActive) setHasBeenActive(true)
  }, [isActive])

  // Default to showing every recorded location (zoom-fit to all). The flip
  // switch toggles between latest-only and all locations; the path has its
  // own checkbox and is on by default.
  const [showAllPins, setShowAllPins] = useState(true)
  const [showPath, setShowPath] = useState(true)

  return (
    <VisionCard>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Heading size="md" color={sc.heading}>
          {t("tags:detail.tabMap")}
        </Heading>
        <HStack gap={4} align="center" wrap="wrap">
          {/* Flip switch: Latest ⟷ All locations (zoom follows the mode). */}
          <HStack gap={2} align="center">
            <Text
              fontSize="xs"
              color={showAllPins ? sc.textMuted : sc.heading}
              fontWeight={showAllPins ? "normal" : "600"}
            >
              {t("tags:detail.mapLatest")}
            </Text>
            <Box
              as="button"
              onClick={() => setShowAllPins((v) => !v)}
              title={t("tags:detail.showAllLocationsHint")}
              w="40px"
              h="22px"
              borderRadius="full"
              bg={showAllPins ? "blue.400" : sc.textMuted}
              position="relative"
              transition="background 0.15s"
              flexShrink={0}
            >
              <Box
                position="absolute"
                top="2px"
                left={showAllPins ? "20px" : "2px"}
                w="18px"
                h="18px"
                borderRadius="full"
                bg="white"
                transition="left 0.15s"
              />
            </Box>
            <Text
              fontSize="xs"
              color={showAllPins ? sc.heading : sc.textMuted}
              fontWeight={showAllPins ? "600" : "normal"}
            >
              {t("tags:detail.showAllLocations")}
              {points.length > 0 && ` (${points.length})`}
            </Text>
          </HStack>
          {/* Path checkbox — independent of the latest/all mode. */}
          <HStack
            as="button"
            gap={2}
            align="center"
            cursor="pointer"
            onClick={() => setShowPath((v) => !v)}
          >
            <Box
              w="18px"
              h="18px"
              borderRadius="4px"
              border="2px solid"
              borderColor={showPath ? "blue.400" : sc.textMuted}
              bg={showPath ? "blue.400" : "transparent"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              color="white"
              fontWeight="bold"
            >
              {showPath && "✓"}
            </Box>
            <Text fontSize="xs" color={sc.textMuted}>
              {t("tags:detail.mapShowPath")}
            </Text>
          </HStack>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => refreshRef.current?.()}
          >
            <FiRefreshCw /> {t("tags:detail.refreshMap")}
          </VisionButton>
        </HStack>
      </Flex>
      {hasBeenActive ? (
        <MapErrorBoundary t={t} sc={sc}>
          <TagMap
            refreshRef={refreshRef}
            points={points}
            formatTimestamp={formatTimestamp}
            isActive={isActive}
            showAllPins={showAllPins}
            showPath={showPath}
          />
        </MapErrorBoundary>
      ) : (
        <Box h="500px" />
      )}
      {/* How-to footer: enumerate every way to interact with the map. */}
      <Box
        mt={3}
        pt={3}
        borderTop="1px solid"
        borderColor="rgba(255,255,255,0.08)"
      >
        <Text
          fontSize="xs"
          color={sc.textMuted}
          textTransform="uppercase"
          letterSpacing="wider"
          mb={1.5}
        >
          {t("tags:detail.mapHelpTitle")}
        </Text>
        <Box
          as="ul"
          display="flex"
          flexWrap="wrap"
          gap={2}
          listStyleType="none"
          m={0}
          p={0}
        >
          {[
            t("tags:detail.mapHelpScroll"),
            t("tags:detail.mapHelpPinch"),
            t("tags:detail.mapHelpDoubleTap"),
            t("tags:detail.mapHelpDrag"),
            t("tags:detail.mapHelpBoxZoom"),
            t("tags:detail.mapHelpShiftDrag"),
            t("tags:detail.mapHelpMarker"),
            t("tags:detail.mapHelpToggle"),
            t("tags:detail.mapHelpControls"),
          ].map((tip) => (
            <Text
              as="li"
              key={tip}
              fontSize="xs"
              color={sc.textSecondary ?? sc.textMuted}
              bg="rgba(255,255,255,0.04)"
              borderRadius="6px"
              px={2}
              py={1}
            >
              {tip}
            </Text>
          ))}
        </Box>
      </Box>
    </VisionCard>
  )
}

// Without a boundary, anything thrown by leaflet or its React wrappers takes
// down the entire tag-detail layout (there's no app-level ErrorBoundary). Trap
// failures here so the rest of the page keeps rendering, and log the React
// component stack to the console so the offending component is identifiable
// even in production minified builds.
interface MapErrorBoundaryProps {
  children: ReactNode
  t: (key: string) => string
  sc: Record<string, string>
}

interface MapErrorBoundaryState {
  error: Error | null
}

class MapErrorBoundary extends Component<
  MapErrorBoundaryProps,
  MapErrorBoundaryState
> {
  state: MapErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): MapErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      "[TagMap] crashed:",
      error,
      "\nComponent stack:",
      info.componentStack,
    )
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          h="500px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Text color={this.props.sc.textMuted} fontSize="sm">
            {this.props.t("tags:detail.mapFailed") ||
              "Map failed to load — see browser console for details."}
          </Text>
        </Box>
      )
    }
    return this.props.children
  }
}

// Box-zoom on demand, implemented with only the public Leaflet API so it
// behaves predictably: while `armed`, map panning is disabled and a plain
// left-drag anywhere on the map draws a hollow rectangle. On release the
// map fits to that rectangle's geographic bounds and the tool disarms.
// A tiny drag (a click) is ignored so a mis-click just disarms.
function BoxZoomHandler({
  armed,
  onComplete,
}: {
  armed: boolean
  onComplete: () => void
}) {
  const map = useMap()

  useEffect(() => {
    if (!armed) return
    const container = map.getContainer()
    const prevCursor = container.style.cursor
    container.style.cursor = "crosshair"
    // Suspend panning + native shift box-zoom while our tool is active.
    map.dragging.disable()
    map.boxZoom.disable()

    let startPt: L.Point | null = null
    let frame: HTMLDivElement | null = null

    const place = (a: L.Point, b: L.Point) => {
      if (!frame) return
      frame.style.left = `${Math.min(a.x, b.x)}px`
      frame.style.top = `${Math.min(a.y, b.y)}px`
      frame.style.width = `${Math.abs(a.x - b.x)}px`
      frame.style.height = `${Math.abs(a.y - b.y)}px`
    }

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      e.preventDefault()
      startPt = map.mouseEventToContainerPoint(e)
      frame = document.createElement("div")
      frame.style.cssText = [
        "position:absolute",
        "border:2px solid #34a1dd",
        "background:rgba(52,161,221,0.10)",
        "pointer-events:none",
        "z-index:900",
        "left:0;top:0;width:0;height:0",
      ].join(";")
      container.appendChild(frame)
      place(startPt, startPt)
    }

    const onMove = (e: MouseEvent) => {
      if (!startPt) return
      place(startPt, map.mouseEventToContainerPoint(e))
    }

    const finish = (e: MouseEvent) => {
      if (!startPt) return
      const endPt = map.mouseEventToContainerPoint(e)
      const dragged =
        Math.abs(endPt.x - startPt.x) > 8 && Math.abs(endPt.y - startPt.y) > 8
      if (frame) {
        frame.remove()
        frame = null
      }
      if (dragged) {
        const bounds = L.latLngBounds(
          map.containerPointToLatLng(startPt),
          map.containerPointToLatLng(endPt),
        )
        map.fitBounds(bounds)
      }
      startPt = null
      onComplete() // single-shot: disarm after one box (or one stray click)
    }

    container.addEventListener("mousedown", onDown)
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", finish)
    return () => {
      container.style.cursor = prevCursor
      map.dragging.enable()
      map.boxZoom.enable()
      if (frame) frame.remove()
      container.removeEventListener("mousedown", onDown)
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", finish)
    }
  }, [armed, map, onComplete])

  return null
}

function TagMap({
  points,
  formatTimestamp,
  refreshRef,
  isActive,
  showAllPins,
  showPath,
}: {
  points: GpsPoint[]
  formatTimestamp: (ts: string) => string
  refreshRef: React.MutableRefObject<(() => void) | null>
  isActive: boolean
  showAllPins: boolean
  showPath: boolean
}) {
  if (points.length === 0) return null

  // Center on the most recent point (points[0] is newest).
  const center: [number, number] = [points[0].lat, points[0].lon]

  // Pins to render: every location when "All locations" is on, otherwise
  // just the latest position.
  const pinPoints = showAllPins ? points : [points[0]]

  // Time-ordered travel path (oldest → newest) — independent of the pin
  // mode, gated by its own checkbox. Sorted copy so the pin/center logic
  // above keeps using the newest-first array.
  const pathLatLngs: [number, number][] = showPath
    ? [...points]
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        )
        .map((p) => [p.lat, p.lon] as [number, number])
    : []

  const tempOf = (pt: GpsPoint): number | null => {
    const raw = pt.data?.temperature
    return typeof raw === "number" ? raw : null
  }

  // Box-zoom: when armed, the next drag on the map draws a rectangle and
  // the map fits to it (like Leaflet's shift-drag, but via a button).
  const [boxZoomArmed, setBoxZoomArmed] = useState(false)

  return (
    <Box h="500px" borderRadius="12px" overflow="hidden" position="relative">
      {/* Overlay zoom-box button (above Leaflet panes via zIndex). */}
      <Box
        as="button"
        position="absolute"
        top="10px"
        right="10px"
        zIndex={1000}
        w="34px"
        h="34px"
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={boxZoomArmed ? "blue.400" : "white"}
        color={boxZoomArmed ? "white" : "#333"}
        border="2px solid rgba(0,0,0,0.2)"
        boxShadow="0 1px 4px rgba(0,0,0,0.3)"
        cursor="pointer"
        title="Draw a box to zoom"
        onClick={() => setBoxZoomArmed((v) => !v)}
      >
        <FiSquare />
      </Box>
      <MapContainer
        center={center}
        zoom={16}
        style={{ height: "100%", width: "100%" }}
      >
        <MapController
          points={points}
          isActive={isActive}
          refreshRef={refreshRef}
          showAllPins={showAllPins}
        />
        <BoxZoomHandler
          armed={boxZoomArmed}
          onComplete={() => setBoxZoomArmed(false)}
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Travel path: chronological polyline connecting every location. */}
        {pathLatLngs.length > 1 && (
          <Polyline
            positions={pathLatLngs}
            pathOptions={{ color: "#34a1dd", weight: 3, opacity: 0.7 }}
          />
        )}
        {pinPoints.map((pt, idx) => {
          const temp = tempOf(pt)
          return (
            <Marker key={`${pt.timestamp}-${idx}`} position={[pt.lat, pt.lon]}>
              <Popup>
                <div style={{ fontSize: "12px", lineHeight: 1.5 }}>
                  <strong>{formatTimestamp(pt.timestamp)}</strong>
                  <br />
                  {pt.lat.toFixed(6)}, {pt.lon.toFixed(6)}
                  {temp != null && (
                    <>
                      <br />
                      Temperature: {temp.toFixed(1)} °C
                    </>
                  )}
                  {pt.data?.rssi != null && (
                    <>
                      <br />
                      RSSI: {pt.data.rssi} dBm
                    </>
                  )}
                  {pt.data?.battery_voltage != null && (
                    <>
                      <br />
                      Battery: {Number(pt.data.battery_voltage).toFixed(2)} V
                    </>
                  )}
                  {pt.data?.state && (
                    <>
                      <br />
                      State: {pt.data.state}
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>
    </Box>
  )
}

/* ═══════════════════════════════════════════════════════════════════════ */
/* Asset Details — shows tag fields with template fallback               */
/* ═══════════════════════════════════════════════════════════════════════ */

import type { TagRead, TagTemplateRead } from "@/client"

function TagField({
  label,
  value,
  mono,
  href,
  sc,
}: {
  label: string
  value: string | number | null | undefined
  mono?: boolean
  href?: string
  sc: Record<string, string>
}) {
  const display = value != null && value !== "" ? String(value) : "—"
  return (
    <Box>
      <Text
        fontSize="xs"
        color={sc.textMuted}
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {label}
      </Text>
      {href && display !== "—" ? (
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          color={sc.heading}
          fontSize="sm"
          mt={1}
          fontFamily={mono ? "mono" : undefined}
          wordBreak="break-all"
        >
          {display}
        </Link>
      ) : (
        <Text
          color={display !== "—" ? sc.heading : sc.textMuted}
          fontSize="sm"
          mt={1}
          fontFamily={mono ? "mono" : undefined}
        >
          {display}
        </Text>
      )}
    </Box>
  )
}

function AssetDetails({
  tag,
  tpl,
  sc,
  t,
  tagId,
}: {
  tag: TagRead
  tpl: TagTemplateRead | null | undefined
  sc: Record<string, string>
  t: any
  tagId: string
}) {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  // Full-size image lightbox: holds the src of the image being previewed.
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  // Force a download even when the image is served from a different origin
  // (the API host), where the anchor `download` attribute is ignored — fetch
  // the bytes and trigger a same-origin blob download.
  const handleDownloadImage = async (src: string) => {
    try {
      const res = await fetch(src)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = src.split("/").pop() || "image.jpg"
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      // Fallback: open the image in a new tab if the fetch is blocked.
      window.open(src, "_blank", "noopener,noreferrer")
    }
  }

  // Barcode + multimodal scan provenance — only meaningful once the tag has
  // been touched by a barcode/MULTIMODAL payload, so both queries are cheap and
  // simply render nothing when empty.
  const { data: barcodeScans = [] } = useQuery({
    queryKey: ["tag-barcode-scans", tagId, tag.organisation_id],
    queryFn: () =>
      TagsService.readOrganizationTagBarcodeScans({
        organisationId: tag.organisation_id,
        id: tagId,
        limit: 50,
      }),
    enabled: !!tagId && !!tag.organisation_id,
  })
  const { data: multimodalScans = [] } = useQuery({
    queryKey: ["tag-multimodal-scans", tagId, tag.organisation_id],
    queryFn: () =>
      TagsService.readOrganizationTagMultimodalScans({
        organisationId: tag.organisation_id,
        id: tagId,
        limit: 20,
      }),
    enabled: !!tagId && !!tag.organisation_id,
  })

  const description = tag.description || tpl?.description
  const category = tag.category || tpl?.category
  const building = tag.location_building || tpl?.location_building
  const floor = tag.location_floor || tpl?.location_floor
  const zone = tag.location_zone || tpl?.location_zone
  const room = tag.location_room || tpl?.location_room
  const assignedTo = tag.assigned_to || tpl?.assigned_to
  const assetStatus = tag.asset_status || tpl?.asset_status
  const notes = tag.notes || tpl?.notes

  const base = (OpenAPI.BASE || "").replace(/\/$/, "")

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const token = localStorage.getItem("access_token")
      const form = new FormData()
      form.append("file", file)
      const res = await fetch(
        `${base}/api/v1/organizations/${tag.organisation_id}/tags/${tagId}/upload-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `Upload failed: ${res.status}`)
      }
      await queryClient.invalidateQueries({ queryKey: ["tag", tagId] })
      showToast.showSuccessToast(
        t("tags:detail.imageUploaded", "Image uploaded"),
      )
    } catch (err: any) {
      showToast.showErrorToast(
        err.message || t("tags:detail.imageUploadError", "Upload failed"),
      )
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleDeleteImage = async (idx: number) => {
    try {
      const token = localStorage.getItem("access_token")
      const res = await fetch(
        `${base}/api/v1/organizations/${tag.organisation_id}/tags/${tagId}/images/${idx}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`)
      await queryClient.invalidateQueries({ queryKey: ["tag", tagId] })
      showToast.showSuccessToast(t("tags:detail.imageDeleted", "Image deleted"))
    } catch (err: any) {
      showToast.showErrorToast(
        err.message || t("tags:detail.imageDeleteError", "Delete failed"),
      )
    }
  }

  const lastLocation =
    tag.last_lat != null && tag.last_lon != null
      ? `${tag.last_lat.toFixed(5)}, ${tag.last_lon.toFixed(5)}${tag.last_altitude != null ? ` (${tag.last_altitude.toFixed(1)} m)` : ""}`
      : null

  return (
    <VStack gap={6} w="100%">
      {/* RFID Tag Information */}
      <VisionCard w="100%">
        <Heading size="sm" color={sc.heading} mb={4}>
          {t("tags:detail.tagInfo")}
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <TagField
            label={t("tags:detail.epc")}
            value={tag.epcid}
            mono
            sc={sc}
          />
          <TagField
            label="TID"
            value={tag.tid ? tag.tid.slice(0, 32) : null}
            mono
            sc={sc}
          />
          <TagField
            label={t("tags:detail.url")}
            value={tag.url}
            href={tag.url || undefined}
            mono
            sc={sc}
          />
          <TagField
            label={t("tags:detail.tagType")}
            value={tag.tag_type}
            sc={sc}
          />
          <TagField
            label={t("tags:detail.chipType")}
            value={tag.chip_type}
            sc={sc}
          />
          <TagField
            label={t("tags:detail.tagModel")}
            value={tag.tag_model}
            sc={sc}
          />
          <TagField
            label={t("tags:detail.state")}
            value={
              tag.state
                ? t(`tags:states.${tag.state}`, { defaultValue: tag.state })
                : null
            }
            sc={sc}
          />
          <TagField
            label={t("tags:detail.battery")}
            value={
              tag.battery_status != null
                ? `${(tag.battery_status / 1000).toFixed(2)} V`
                : null
            }
            sc={sc}
          />
          <TagField
            label={t("tags:detail.lastSeen")}
            value={
              tag.last_seen_at
                ? format(new Date(tag.last_seen_at), "PPp")
                : null
            }
            sc={sc}
          />
          <TagField
            label={t("tags:detail.lastLocation")}
            value={lastLocation}
            sc={sc}
          />
        </Grid>

        {/* Alarm parameters (thresholds + warning margin) */}
        {(tag.min_threshold != null ||
          tag.max_threshold != null ||
          tag.template?.warning_margin != null) && (
          <>
            <Heading size="xs" color={sc.heading} mt={6} mb={3}>
              {t("tags:detail.alarmParameters", "Alarm parameters")}
            </Heading>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <TagField
                label={t("tags:form.minThreshold")}
                value={
                  tag.min_threshold != null
                    ? `${tag.min_threshold}${tag.template?.unit ?? ""}`
                    : null
                }
                sc={sc}
              />
              <TagField
                label={t("tags:form.maxThreshold")}
                value={
                  tag.max_threshold != null
                    ? `${tag.max_threshold}${tag.template?.unit ?? ""}`
                    : null
                }
                sc={sc}
              />
              {tag.template?.warning_margin != null && (
                <TagField
                  label={t("tags:detail.warningMargin", "Warning margin")}
                  value={`${(Number(tag.template.warning_margin) * 100).toFixed(1)}%`}
                  sc={sc}
                />
              )}
            </Grid>
          </>
        )}

        {/* Logging parameters (from configuration JSON / template) */}
        {tag.configuration && Object.keys(tag.configuration).length > 0 && (
          <>
            <Heading size="xs" color={sc.heading} mt={6} mb={3}>
              {t("tags:detail.loggingParameters", "Logging parameters")}
            </Heading>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              {Object.entries(tag.configuration).map(([k, v]) => {
                // Epoch timestamp keys (e.g. arm_time) arrive as a raw epoch
                // from the logger — render them as a local date/time. Guard on
                // a plausibly-epoch magnitude, and accept seconds or ms.
                const isEpoch =
                  /(^|_)time$/.test(k) &&
                  v != null &&
                  Number.isFinite(Number(v)) &&
                  Number(v) > 1e8
                let display: string | null
                if (v === null || v === undefined) {
                  display = null
                } else if (isEpoch) {
                  const n = Number(v)
                  display = new Date(n < 1e12 ? n * 1000 : n).toLocaleString()
                } else {
                  display = String(v)
                }
                return (
                  <TagField key={k} label={k} value={display} mono sc={sc} />
                )
              })}
            </Grid>
          </>
        )}
      </VisionCard>

      {/* Asset Info */}
      <VisionCard w="100%">
        <Heading size="sm" color={sc.heading} mb={4}>
          {t("tags:form.assetInfo")}
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mb={4}>
          <TagField
            label={t("tags:form.description")}
            value={description}
            sc={sc}
          />
          <TagField label={t("tags:form.category")} value={category} sc={sc} />
          <TagField
            label={t("tags:form.serialNumber")}
            value={tag.serial_number}
            mono
            sc={sc}
          />
          <TagField label={t("tags:form.sku")} value={tag.sku} mono sc={sc} />
        </Grid>
        <HStack gap={3} flexWrap="wrap" alignItems="flex-start">
          {(tag.images ?? []).map((img, idx) => {
            const src = img.startsWith("http")
              ? img
              : `${base}/api/v1/uploads/${img}`
            return (
              <Box
                key={img}
                position="relative"
                w="120px"
                h="120px"
                borderRadius="8px"
                overflow="hidden"
                border="1px solid"
                borderColor={sc.border}
              >
                <Box
                  as="button"
                  display="block"
                  w="100%"
                  h="100%"
                  p={0}
                  cursor="zoom-in"
                  onClick={() => setPreviewImage(src)}
                >
                  <img
                    src={src}
                    alt={`${idx + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Box
                  position="absolute"
                  top="4px"
                  right="4px"
                  cursor="pointer"
                  bg="rgba(0,0,0,0.55)"
                  borderRadius="full"
                  p="4px"
                  _hover={{ bg: "rgba(200,50,50,0.8)" }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteImage(idx)
                  }}
                >
                  <FiTrash2 size={12} color="white" />
                </Box>
              </Box>
            )
          })}
          <Box
            as="button"
            w="120px"
            h="120px"
            borderRadius="8px"
            border="1px dashed"
            borderColor={sc.border}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="4px"
            color={sc.textMuted}
            _hover={{ borderColor: sc.heading, color: sc.heading }}
            cursor={uploading ? "wait" : "pointer"}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? <Spinner size="sm" /> : <FiPlus size={20} />}
            <Text fontSize="xs">
              {uploading
                ? t("common:loading", "…")
                : t("tags:detail.addImage", "Add image")}
            </Text>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: "none" }}
            onChange={handleUpload}
          />
        </HStack>

        {/* Full-size image lightbox: click a thumbnail to view it full size,
            download it, or close (backdrop click / Esc / the × button). */}
        <DialogRoot
          open={previewImage !== null}
          onOpenChange={(e) => !e.open && setPreviewImage(null)}
          size="cover"
          placement="center"
        >
          <DialogBackdrop />
          <DialogContent bg="transparent" boxShadow="none" maxW="100vw">
            <DialogCloseTrigger
              color="white"
              bg="rgba(0,0,0,0.6)"
              borderRadius="full"
              boxSize="36px"
              top="3"
              insetEnd="3"
              _hover={{ bg: "rgba(0,0,0,0.85)" }}
            />
            <DialogBody
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={4}
              p={4}
            >
              {previewImage && (
                <>
                  <img
                    src={previewImage}
                    alt={t("tags:detail.imagePreview", "Image preview")}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "82vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                  <HStack gap={3}>
                    <VisionButton
                      onClick={() => handleDownloadImage(previewImage)}
                    >
                      <FiDownload style={{ marginRight: 8 }} />
                      {t("tags:detail.downloadImage", "Download")}
                    </VisionButton>
                    <VisionButton
                      variant="outlineWhite"
                      onClick={() => setPreviewImage(null)}
                    >
                      <FiX style={{ marginRight: 8 }} />
                      {t("tags:detail.close", "Close")}
                    </VisionButton>
                  </HStack>
                </>
              )}
            </DialogBody>
          </DialogContent>
        </DialogRoot>
      </VisionCard>

      {/* Barcode & multimodal scan provenance */}
      {(tag.barcode ||
        barcodeScans.length > 0 ||
        multimodalScans.length > 0) && (
        <VisionCard w="100%">
          <Heading size="sm" color={sc.heading} mb={4}>
            {t("tags:detail.scanProvenance", "Barcode & multimodal scans")}
          </Heading>

          {tag.barcode && (
            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={4}
              mb={4}
            >
              <TagField
                label={t("tags:detail.barcode", "Barcode")}
                value={tag.barcode}
                mono
                sc={sc}
              />
              <TagField
                label={t("tags:detail.symbology", "Symbology")}
                value={tag.barcode_symbology}
                sc={sc}
              />
            </Grid>
          )}

          {barcodeScans.length > 0 && (
            <>
              <Heading size="xs" color={sc.heading} mt={2} mb={2}>
                {t("tags:detail.barcodeScans", "Barcode scans")} (
                {barcodeScans.length})
              </Heading>
              <VStack align="stretch" gap={2} mb={4}>
                {barcodeScans.slice(0, 10).map((b) => (
                  <HStack
                    key={`${b.timestamp}-${b.value}`}
                    justify="space-between"
                    fontSize="sm"
                    color={sc.text}
                  >
                    <Text fontFamily="mono">{b.value}</Text>
                    <Text color={sc.textMuted}>
                      {b.symbology || "—"} ·{" "}
                      {new Date(b.timestamp).toLocaleString()}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </>
          )}

          {multimodalScans.length > 0 && (
            <>
              <Heading size="xs" color={sc.heading} mt={2} mb={2}>
                {t("tags:detail.multimodalScans", "Multimodal scans")} (
                {multimodalScans.length})
              </Heading>
              <VStack align="stretch" gap={3}>
                {multimodalScans.slice(0, 10).map((m) => {
                  const ts = m.scan_completed || m.created_at
                  const ocrText = (m.ocr || [])
                    .map((o) => (o as { text?: string })?.text)
                    .filter(Boolean)
                    .join(" · ")
                  return (
                    <Box key={m.scan_id} fontSize="sm" color={sc.text}>
                      <HStack justify="space-between">
                        <HStack gap={2}>
                          <Badge>{m.anchor_modality || "—"}</Badge>
                          <Text color={sc.textMuted}>
                            {m.codes?.length ?? 0} codes ·{" "}
                            {m.images?.length ?? 0} img · {m.ocr?.length ?? 0}{" "}
                            ocr
                          </Text>
                        </HStack>
                        <Text color={sc.textMuted}>
                          {ts ? new Date(ts).toLocaleString() : "—"}
                        </Text>
                      </HStack>
                      {ocrText && (
                        <Text color={sc.textMuted} fontStyle="italic">
                          {ocrText}
                        </Text>
                      )}
                    </Box>
                  )
                })}
              </VStack>
            </>
          )}
        </VisionCard>
      )}

      {/* Location */}
      <VisionCard w="100%">
        <Heading size="sm" color={sc.heading} mb={4}>
          {t("tags:form.assetLocation")}
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <TagField label={t("tags:form.building")} value={building} sc={sc} />
          <TagField label={t("tags:form.floor")} value={floor} sc={sc} />
          <TagField label={t("tags:form.zone")} value={zone} sc={sc} />
          <TagField label={t("tags:form.room")} value={room} sc={sc} />
        </Grid>
      </VisionCard>

      {/* Management */}
      <VisionCard w="100%">
        <Heading size="sm" color={sc.heading} mb={4}>
          {t("tags:form.assetManagement")}
        </Heading>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <TagField
            label={t("tags:form.assignedTo")}
            value={assignedTo}
            sc={sc}
          />
          <TagField
            label={t("tags:form.acquisitionDate")}
            value={tag.acquisition_date}
            sc={sc}
          />
          <TagField
            label={t("tags:form.assetValue")}
            value={tag.asset_value != null ? `$${tag.asset_value}` : null}
            sc={sc}
          />
          <TagField
            label={t("tags:form.assetStatusField")}
            value={assetStatus}
            sc={sc}
          />
          <Box gridColumn={{ md: "span 2" }}>
            <TagField label={t("tags:form.notes")} value={notes} sc={sc} />
          </Box>
        </Grid>
      </VisionCard>
    </VStack>
  )
}
